import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Users, TrendingUp, AlertCircle, CheckCircle2, Pause, Play, Percent,
  Plus, X, Loader2, FileText, BadgeCheck, Edit3, Link as LinkIcon, ExternalLink,
  Trash2, Copy, Flame, Smartphone, Apple, Upload, FileCode2, Image as ImageIcon, Store, Bell,
  Clapperboard,
} from "lucide-react";
import TenantStatusBoard from "../../components/superadmin/TenantStatusBoard";
import CloneTenantModal from "../../components/superadmin/CloneTenantModal";

const CYCLES = ["monthly", "yearly", "one_time"];

// Per-tenant notification switches (tenant record, super-admin only).
const NOTIFICATION_FEATURES = [
  {
    key: "bulk_notifications_enabled", short: "Bulk", testid: "bulk",
    label: "Bulk notifications",
    help: "Tenant admin can send a notification to an audience immediately (Push Broadcasts, Re-engage dormant customers).",
  },
  {
    key: "scheduled_notifications_enabled", short: "Scheduled", testid: "scheduled",
    label: "Scheduled notifications",
    help: "Tenant admin can schedule notifications for a future time, see them and cancel them.",
  },
];

function StatusPill({ active }) {
  return (
    <span
      className={`text-xs px-2 py-0.5 ${active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
      data-testid={`tenant-status-${active ? "active" : "suspended"}`}>
      {active ? "Active" : "Suspended"}
    </span>
  );
}

function FirebaseUploadTile({ platform, label, icon: Icon, bundleId, status, busy, onUpload, onClear }) {
  const inputId = `fb-upload-${platform}`;
  const isUploading = busy === `upload-${platform}`;
  const isClearing = busy === `clear-${platform}`;
  const source = status?.source;
  const present = status?.present;
  const accept = platform === "android" ? "application/json,.json" : ".plist,application/x-plist,text/xml";

  return (
    <div className="border border-[hsl(var(--kb-border))] p-3 space-y-2 bg-[hsl(var(--kb-surface-alt))]"
         data-testid={`firebase-upload-tile-${platform}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs text-[hsl(var(--kb-muted))] uppercase tracking-wider flex items-center gap-1">
            <Icon size={11} /> {platform === "android" ? "Android" : "iOS"}
          </div>
          <div className="font-mono text-xs mt-1 truncate" title={bundleId || ""}>
            {bundleId || "(no bundle id yet)"}
          </div>
        </div>
        {present ? (
          <span className={`text-[10px] px-1.5 py-0.5 ${source === "manual" ? "bg-orange-100 text-orange-700" : "bg-green-100 text-green-700"}`}
                data-testid={`firebase-source-${platform}`}>
            {source === "manual" ? "MANUAL" : "AUTO"}
          </span>
        ) : (
          <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700">MISSING</span>
        )}
      </div>

      <div className="text-[11px] text-[hsl(var(--kb-muted))] flex items-center gap-1">
        <FileCode2 size={10} /> <code className="font-mono">{label}</code>
      </div>

      {status?.uploaded_at && (
        <div className="text-[10px] text-[hsl(var(--kb-muted))]">
          uploaded {new Date(status.uploaded_at).toLocaleString()}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <input
          id={inputId}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(f);
            e.target.value = ""; // allow re-selecting the same file
          }}
          data-testid={`firebase-file-input-${platform}`}
        />
        <label
          htmlFor={inputId}
          className={`text-xs inline-flex items-center gap-1 px-2 py-1 cursor-pointer border border-[hsl(var(--kb-border))] bg-white hover:bg-[hsl(var(--kb-surface))] ${isUploading ? "opacity-50 pointer-events-none" : ""}`}
          data-testid={`firebase-upload-btn-${platform}`}
        >
          {isUploading ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
          {present ? "Replace" : "Upload"}
        </label>
        {present && source === "manual" && (
          <button
            onClick={onClear}
            disabled={isClearing}
            className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
            data-testid={`firebase-clear-btn-${platform}`}
            title="Remove manual upload (auto-provision will repopulate)"
          >
            {isClearing ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />}
          </button>
        )}
      </div>
    </div>
  );
}


/** Per-tenant "Maximum Admin Members" editor (tenant record, super-admin only). */
function AdminLimitEditor({ tenantRecord, usage, busy, onSave }) {
  const current = usage?.limit ?? 3;
  const [value, setValue] = useState(String(current));
  useEffect(() => { setValue(String(current)); }, [current, tenantRecord?.id]);
  const n = Number(value);
  const valid = /^\d+$/.test(value) && n >= 1 && n <= 50;
  const below = valid && usage && n < usage.active;
  return (
    <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="admin-limit-section">
      <h3 className="font-display text-lg flex items-center gap-2">
        <Users size={14} /> Admin members
      </h3>
      <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">
        Maximum number of <b>active</b> admin / staff accounts for <b>this tenant only</b> (owner included).
        Default is 3. Inactive admins don't use a seat.
      </p>
      <div className="mt-3 text-sm" data-testid="admin-limit-usage">
        Currently <b>{usage ? `${usage.active} / ${usage.limit}` : "—"}</b> admins
        {usage?.inactive ? <span className="text-[hsl(var(--kb-muted))]"> · {usage.inactive} inactive</span> : null}
        {usage?.over_limit ? <span className="text-red-700"> · over limit</span> : null}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div>
          <label htmlFor="admin-limit-input" className="text-xs text-[hsl(var(--kb-muted))] block">Maximum Admin Members</label>
          <input id="admin-limit-input" type="number" min={1} max={50} step={1} inputMode="numeric"
            value={value} onChange={(e) => setValue(e.target.value)}
            className="kb-input w-28 min-h-[40px]" aria-invalid={!valid} data-testid="admin-limit-input" />
        </div>
        <button type="button" className="kb-btn-primary text-sm min-h-[40px]"
          disabled={!valid || busy || n === current} onClick={() => onSave(n)} data-testid="admin-limit-save">
          {busy ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}Save limit
        </button>
      </div>
      {!valid && <div className="text-xs text-red-600 mt-1" data-testid="admin-limit-error">Enter a whole number from 1 to 50.</div>}
      {below && (
        <div className="text-xs text-amber-700 mt-1" data-testid="admin-limit-below">
          This tenant has {usage.active} active admins. Nobody will be deactivated, but they won't be able to add or
          reactivate admins until they are within the new limit.
        </div>
      )}
    </section>
  );
}

export default function SuperAdminTenants() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [features, setFeatures] = useState([]);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [showClone, setShowClone] = useState(false);
  const [createForm, setCreateForm] = useState({
    slug: "", name: "", owner_phone: "", owner_name: "", plan_slug: "pro", custom_domain: "",
  });
  const [cloneOpts, setCloneOpts] = useState({
    categories: true,
    brands: false,
    attributes: true,
    homepage_sections: true,
  });
  const [detailCloneOpts, setDetailCloneOpts] = useState({
    categories: false,
    brands: false,
    attributes: false,
    homepage_sections: false,
  });
  const [detailCloneResult, setDetailCloneResult] = useState(null);

  // ----- Push notifications (per-tenant broadcast) -----
  const [pushForm, setPushForm] = useState({
    title: "", body: "",
    platforms: { android: true, ios: true, web: false },
  });
  const [pushSummary, setPushSummary] = useState(null);
  const [pushResult, setPushResult] = useState(null);
  const [pushHistory, setPushHistory] = useState([]);
  const [pushDiag, setPushDiag] = useState(null);    // device sample + test send result
  const [pushTestToken, setPushTestToken] = useState("");
  const [pushBroadcastDetail, setPushBroadcastDetail] = useState(null);  // {id, loading, data}
  const [autoProvisionFirebase, setAutoProvisionFirebase] = useState(true);
  const [createResult, setCreateResult] = useState(null);
  const [pending, setPending] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/superadmin/pending-tenants");
        if (!cancelled) setPending(data || []);
      } catch {
        if (!cancelled) setPending([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadPending = async () => {
    try {
      const { data } = await api.get("/superadmin/pending-tenants");
      setPending(data || []);
    } catch { setPending([]); }
  };

  const [planPrice, setPlanPrice] = useState("");
  const [planCycle, setPlanCycle] = useState("monthly");
  const [planSlugEdit, setPlanSlugEdit] = useState("");
  const [discountPct, setDiscountPct] = useState("");
  const [discountAmt, setDiscountAmt] = useState("");
  const [discountNote, setDiscountNote] = useState("");
  const [grantKey, setGrantKey] = useState("");
  const [grantCycle, setGrantCycle] = useState("monthly");
  const [grantQty, setGrantQty] = useState(1);
  const [grantPrice, setGrantPrice] = useState("");
  const [grantNote, setGrantNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [t, f, p] = await Promise.all([
          api.get("/superadmin/tenants"),
          api.get("/superadmin/features"),
          api.get("/superadmin/plans"),
        ]);
        if (cancelled) return;
        setRows(t.data);
        setFeatures(f.data);
        setPlans((p.data || []).filter((pl) => pl.is_active));
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Could not load tenants");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = async () => {
    try {
      const [t, f, p] = await Promise.all([
        api.get("/superadmin/tenants"),
        api.get("/superadmin/features"),
        api.get("/superadmin/plans"),
      ]);
      setRows(t.data);
      setFeatures(f.data);
      setPlans((p.data || []).filter((pl) => pl.is_active));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load tenants");
    }
  };

  const [firebaseStatus, setFirebaseStatus] = useState(null);

  const loadFirebaseStatus = async (tenantId) => {
    if (!tenantId) return;
    try {
      const { data } = await api.get(`/superadmin/tenants/${tenantId}/firebase/status`);
      setFirebaseStatus(data);
    } catch { setFirebaseStatus(null); }
  };

  const open = async (tid) => {
    setSelectedId(tid);
    setDetail(null);
    setFirebaseStatus(null);
    try {
      const { data } = await api.get(`/superadmin/tenants/${tid}`);
      setDetail(data);
      setPlanPrice(data.subscription?.price_at_purchase ?? "");
      setPlanCycle(data.subscription?.billing_cycle ?? "monthly");
      setPlanSlugEdit(data.subscription?.plan_slug ?? "");
      setDiscountPct(data.discount?.percent || "");
      setDiscountAmt(data.discount?.amount || "");
      setDiscountNote(data.discount?.note || "");
      setGrantKey(""); setGrantPrice(""); setGrantNote(""); setGrantQty(1); setGrantCycle("monthly");
      const tenantId = data?.tenant_record?.id || data?.tenant?.tenant_id || tid;
      loadFirebaseStatus(tenantId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load detail");
    }
  };
  const close = () => { setSelectedId(null); setDetail(null); setFirebaseStatus(null); };

  const refreshDetail = async () => {
    if (!selectedId) return;
    const { data } = await api.get(`/superadmin/tenants/${selectedId}`);
    setDetail(data);
    const tenantId = data?.tenant_record?.id || data?.tenant?.tenant_id || selectedId;
    loadFirebaseStatus(tenantId);
    loadPushSummary(tenantId);
    loadPushHistory(tenantId);
    await load();
  };

  const loadPushSummary = async (tid) => {
    try {
      const { data } = await api.get(`/superadmin/tenants/${tid}/push/devices/summary`);
      setPushSummary(data);
    } catch (e) {
      setPushSummary({ total: 0, by_platform: {}, distinct_users: 0, error: true });
    }
  };
  const loadPushHistory = async (tid) => {
    try {
      const { data } = await api.get(`/superadmin/tenants/${tid}/push/broadcasts?limit=10`);
      setPushHistory(data || []);
    } catch (e) { /* ignore */ }
  };

  // Diagnostics — pull a small sample of registered tokens (truncated, safe).
  // Used by the operator when the broadcast counter says "Sent X/X" but
  // devices show nothing — gives confidence the right tokens are stored.
  const loadPushDeviceSample = async (tid) => {
    try {
      const { data } = await api.get(`/superadmin/tenants/${tid}/push/devices/sample?limit=5`);
      setPushDiag((d) => ({ ...(d || {}), sample: data?.sample || [], sample_at: new Date().toISOString() }));
    } catch (e) {
      toast.error("Could not load device sample");
    }
  };

  // Fetch the per-device FCM response detail for one historical broadcast.
  // Surfaces the exact Firebase message_id returned for each device — the
  // definitive answer to "Did FCM really accept what we sent?".
  const loadBroadcastDetail = async (tid, bid) => {
    setPushBroadcastDetail({ id: bid, loading: true, data: null });
    try {
      const { data } = await api.get(`/superadmin/tenants/${tid}/push/broadcasts/${bid}`);
      setPushBroadcastDetail({ id: bid, loading: false, data });
    } catch (e) {
      setPushBroadcastDetail({ id: bid, loading: false, data: null, error: e?.response?.data?.detail || "Failed to load" });
    }
  };

  // Fire a single test push against an explicit token; the endpoint
  // returns the FULL FCM v1 response so we can see exactly what
  // Firebase says (success message_id OR error code).
  const sendPushTest = async (tid) => {
    if (!pushTestToken.trim()) return toast.error("Paste a token first");
    setPushDiag((d) => ({ ...(d || {}), test_busy: true }));
    try {
      const { data } = await api.post(`/superadmin/tenants/${tid}/push/send-test`, {
        token: pushTestToken.trim(),
        title: "Test push",
        body: "If you see this, end-to-end FCM delivery is working.",
      });
      setPushDiag((d) => ({ ...(d || {}), test_result: data, test_busy: false, test_at: new Date().toISOString() }));
      if (data.ok) toast.success("FCM accepted — check the device now");
      else toast.error(`FCM rejected: ${data.fcm_status || ""} — see details below`);
    } catch (e) {
      setPushDiag((d) => ({ ...(d || {}), test_busy: false, test_result: { ok: false, error: e?.response?.data?.detail || e.message } }));
      toast.error("Test send failed");
    }
  };

  const sendPush = (dryRun = false) =>
    run(dryRun ? "pushPreview" : "pushSend", async () => {
      const tid = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
      if (!tid) { toast.error("Could not resolve tenant id"); return; }
      const plats = Object.entries(pushForm.platforms)
        .filter(([, v]) => v).map(([k]) => k);
      if (!plats.length) return toast.error("Pick at least one platform");
      if (!dryRun) {
        if (!pushForm.title.trim() || !pushForm.body.trim()) {
          return toast.error("Title and body are required to send");
        }
      }
      setPushResult(null);
      const payload = {
        title: pushForm.title.trim() || "(preview)",
        body: pushForm.body.trim() || "(preview)",
        platforms: plats,
        dry_run: dryRun,
      };
      const { data } = await api.post(
        `/superadmin/tenants/${tid}/push/broadcast`, payload,
      );
      setPushResult(data);
      if (dryRun) {
        toast.info(`Will reach ${data.matched} device(s)`);
      } else {
        toast.success(`Sent to ${data.sent}/${data.matched} device(s)`);
        loadPushHistory(tid);
        loadPushSummary(tid);
      }
    });

  const run = async (label, fn) => {
    setBusy(label);
    try { await fn(); } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const toggleSuspend = () =>
    run("suspend", async () => {
      if (detail.is_suspended) {
        await api.post(`/superadmin/tenants/${selectedId}/activate`);
        toast.success("Tenant activated");
      } else {
        const reason = window.prompt("Reason for suspension (optional)") || "";
        await api.post(`/superadmin/tenants/${selectedId}/suspend`, { reason });
        toast.success("Tenant suspended");
      }
      await refreshDetail();
    });

  const impersonateTenant = () =>
    run("impersonate", async () => {
      const confirmed = window.confirm(
        `Log in as the admin of "${detail.tenant.name}"?\n\n` +
        "Your current super-admin session will be replaced. Re-login " +
        "with 9858558555 / 557725 to return.",
      );
      if (!confirmed) return;
      const { data } = await api.post(`/superadmin/tenants/${selectedId}/impersonate`);
      // Drop the new token into local storage and reload — same flow as
      // a fresh OTP login. AuthContext will pick it up on first paint.
      localStorage.setItem("kb_token", data.token);
      // Pin the X-Tenant-Slug header for every subsequent request so we
      // address the impersonated tenant's data, not the default tenant
      // (the current URL is /superadmin/... which has no /t/{slug}
      // prefix to resolve from).
      const tenantSlug = data?.tenant?.slug;
      if (tenantSlug) localStorage.setItem("kb_tenant", tenantSlug);
      toast.success(`Now logged in as ${data.user.phone}`);
      // Build the final admin URL on the tenant's storefront so the
      // BrowserRouter `basename` picks up `/t/{slug}` and every page
      // calls the right tenant's API. Falls back gracefully to the
      // current origin if no storefront_url is configured.
      const target = (() => {
        const sf = detail?.storefront_url;
        if (sf && tenantSlug) {
          // Normalize to "{origin}/t/{slug}/admin".
          try {
            const u = new URL(sf);
            return `${u.origin}/t/${tenantSlug}/admin`;
          } catch { /* fall through */ }
        }
        if (tenantSlug) return `${window.location.origin}/t/${tenantSlug}/admin`;
        return `${window.location.origin}/admin`;
      })();
      setTimeout(() => { window.location.href = target; }, 400);
    });

  const savePlan = () =>
    run("plan", async () => {
      const body = {};
      if (planPrice !== "" && Number(planPrice) >= 0) body.price_at_purchase = Number(planPrice);
      if (planCycle && planCycle !== detail.subscription?.billing_cycle) body.billing_cycle = planCycle;
      if (planSlugEdit && planSlugEdit !== detail.subscription?.plan_slug) body.plan_slug = planSlugEdit;
      if (Object.keys(body).length === 0) { toast.info("Nothing changed"); return; }
      await api.patch(`/superadmin/tenants/${selectedId}/subscription`, body);
      toast.success(body.plan_slug ? "Plan switched" : "Plan updated");
      await refreshDetail();
    });

  const saveBusinessMode = (mode) =>
    run("businessMode", async () => {
      const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
      if (!tenantId) { toast.error("Could not resolve tenant id"); return; }
      await api.put(`/superadmin/tenants/${tenantId}/business-mode`, { business_mode: mode });
      toast.success(`Switched to ${mode === "single_vendor" ? "Single Vendor" : "Multi Vendor"} mode`);
      await refreshDetail();
    });

  const saveAdminLimit = (maxAdmins) =>
    run("adminLimit", async () => {
      const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
      if (!tenantId) { toast.error("Could not resolve tenant id"); return; }
      await api.put(`/superadmin/tenants/${tenantId}/admin-limit`, { max_admins: maxAdmins });
      toast.success(`Admin limit set to ${maxAdmins} for this tenant`);
      await refreshDetail();
    });

  const saveNotificationFeature = (key, enabled) =>
    run(`notif:${key}`, async () => {
      const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
      if (!tenantId) { toast.error("Could not resolve tenant id"); return; }
      await api.put(`/superadmin/tenants/${tenantId}/notification-features`, { [key]: enabled });
      const f = NOTIFICATION_FEATURES.find((x) => x.key === key);
      toast.success(`${f.label} ${enabled ? "enabled" : "disabled"} for this tenant`);
      await refreshDetail();
      await load();
    });

  const saveReelsEnabled = (enabled) =>
    run("reels", async () => {
      const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
      if (!tenantId) { toast.error("Could not resolve tenant id"); return; }
      await api.put(`/superadmin/tenants/${tenantId}/reels`, { reels_enabled: enabled });
      toast.success(enabled ? "Reels enabled for this tenant" : "Reels disabled for this tenant");
      await refreshDetail();
      await load();
    });

  const runDetailClone = () =>
    run("clone", async () => {
      const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
      if (!tenantId) { toast.error("Could not resolve tenant id"); return; }
      if (!Object.values(detailCloneOpts).some(Boolean)) {
        toast.error("Pick at least one collection to clone");
        return;
      }
      if (tenantId === detail?.tenant_record?.id &&
          detail?.tenant_record?.slug === "default") {
        toast.error("Cannot clone the default tenant onto itself");
        return;
      }
      setDetailCloneResult(null);
      const { data } = await api.post(
        `/superadmin/tenants/${tenantId}/clone-from-default`, detailCloneOpts,
      );
      setDetailCloneResult(data);
      const total = Object.entries(data || {})
        .filter(([, v]) => typeof v === "number")
        .reduce((a, [, v]) => a + v, 0);
      toast.success(`Cloned ${total} item(s) from default tenant`);
    });

  const deleteTenant = () =>
    run("delete", async () => {
      const name = detail?.tenant_record?.name || detail?.tenant?.name || "this tenant";
      if (!window.confirm(`Permanently delete "${name}" and ALL its data?\nThis cannot be undone.`)) return;
      const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
      if (!tenantId) {
        toast.error("Could not resolve tenant id");
        return;
      }
      try {
        await api.delete(`/superadmin/tenants/${tenantId}/hard-delete`);
        toast.success("Tenant deleted");
        close();
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Delete failed");
      }
    });

  const saveDiscount = () =>
    run("discount", async () => {
      await api.post(`/superadmin/tenants/${selectedId}/discount`, {
        discount_percent: Number(discountPct) || 0,
        discount_amount: Number(discountAmt) || 0,
        note: discountNote || null,
      });
      toast.success("Discount saved");
      await refreshDetail();
    });

  const clearDiscount = () =>
    run("clearDiscount", async () => {
      await api.delete(`/superadmin/tenants/${selectedId}/discount`);
      setDiscountPct(""); setDiscountAmt(""); setDiscountNote("");
      toast.success("Discount cleared");
      await refreshDetail();
    });

  const provisionFirebase = () => {
    const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
    if (!tenantId) { toast.error("Tenant id missing"); return; }
    const isReprovision = !!detail?.tenant_record?.firebase_provisioned_at;
    const exec = () =>
      run("firebase", async () => {
        try {
          const { data } = await api.post(`/superadmin/tenants/${tenantId}/firebase/provision`);
          toast.success(`Provisioned: ${data.android_bundle}`);
          await refreshDetail();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Firebase provision failed");
        }
      });
    if (!isReprovision) { exec(); return; }
    confirmFirebaseChange({
      title: "Re-provision Firebase apps?",
      summary: `This will recreate the Android & iOS apps in the assigned Firebase shard. The project ID stays the same, but new App IDs may be issued.`,
      bullets: [
        "All installed apps (Android + iOS) will keep working temporarily.",
        "FCM tokens may rotate — broadcast delivery counts can dip until devices re-register.",
        "Re-run `python scripts/build_app.py --tenant <slug>` to bake the refreshed google-services.json / GoogleService-Info.plist into the next native build.",
      ],
      confirmLabel: "Yes, re-provision",
      onConfirm: exec,
    });
  };

  const uploadFirebaseConfig = async (platform, file) => {
    const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
    if (!tenantId || !file) return;
    const exec = async () => {
      const key = `upload-${platform}`;
      setBusy(key);
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post(
          `/superadmin/tenants/${tenantId}/firebase/upload/${platform}`,
          form
        );
        toast.success(`${platform === "android" ? "google-services.json" : "GoogleService-Info.plist"} uploaded (${data.bytes} bytes)`);
        await refreshDetail();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
      } finally { setBusy(null); }
    };
    // First-time upload — proceed without prompting.
    const status = platform === "android" ? firebaseStatus?.android : firebaseStatus?.ios;
    if (!status?.present) { await exec(); return; }
    const filename = platform === "android" ? "google-services.json" : "GoogleService-Info.plist";
    confirmFirebaseChange({
      title: `Replace ${filename}?`,
      summary: `A ${filename} is already on file for this tenant. Uploading a new one will overwrite the current config used by the next native build.`,
      bullets: [
        "Installed apps continue to work with their existing FCM token until they're updated from Play/App Store.",
        "If the new file points at a different Firebase project, push tokens issued from the old project will silently stop arriving — users will need to update the app.",
        "Run `python scripts/build_app.py --tenant <slug>` after replacing so the new file is baked into the next build.",
      ],
      confirmLabel: `Yes, replace ${filename}`,
      onConfirm: exec,
    });
  };

  const clearFirebaseConfig = async (platform) => {
    const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
    if (!tenantId) return;
    if (!window.confirm(`Remove the uploaded ${platform === "android" ? "google-services.json" : "GoogleService-Info.plist"}? Auto-provision will populate it again on next run.`)) return;
    const key = `clear-${platform}`;
    setBusy(key);
    try {
      await api.delete(`/superadmin/tenants/${tenantId}/firebase/upload/${platform}`);
      toast.success("Removed");
      await refreshDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    } finally { setBusy(null); }
  };

  // ---- Dual-mode (Manual / Auto) Firebase handlers --------------------
  const [manualForm, setManualForm] = useState({
    project_id: "",
    android_app_id: "",
    ios_app_id: "",
    android_package_name: "",
    ios_bundle_id: "",
    web_api_key: "",
    storage_bucket: "",
    service_account_json: "",
  });
  const [showManualWarning, setShowManualWarning] = useState(false);
  const [pendingManualSwitch, setPendingManualSwitch] = useState(false);

  // Generic "are-you-sure" gate for actions that mutate an already-live
  // Firebase config (re-provision, replace google-services.json/plist,
  // overwrite manual project ID). Push tokens issued by Firebase are
  // pinned to (project_id, app_id), so changing any of these silently
  // breaks delivery for every installed device until users update the
  // app from Play/App Store. We block the action behind an explicit
  // confirm with the exact consequences spelled out.
  const [fbWarn, setFbWarn] = useState(null);
  // shape: { title, summary, bullets: [], confirmLabel, onConfirm }
  const confirmFirebaseChange = (cfg) => setFbWarn(cfg);

  // Hydrate the manual form whenever a different tenant's status arrives.
  useEffect(() => {
    if (!firebaseStatus) return;
    const m = firebaseStatus.manual || {};
    setManualForm((prev) => ({
      ...prev,
      project_id: m.project_id || "",
      android_app_id: m.android_app_id || "",
      ios_app_id: m.ios_app_id || "",
      android_package_name: detail?.tenant_record?.android_package_name || "",
      ios_bundle_id: detail?.tenant_record?.ios_bundle_id || "",
      web_api_key: "",
      storage_bucket: m.storage_bucket || "",
      service_account_json: "",
    }));
  }, [firebaseStatus, detail?.tenant_record?.android_package_name, detail?.tenant_record?.ios_bundle_id]);

  const saveManualFirebase = () => {
    const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
    if (!tenantId) { toast.error("Tenant id missing"); return; }
    const f = manualForm;
    const form = new FormData();
    // Send only fields the user actually filled — partial updates preserve
    // existing values on the backend (per Answer 1.b).
    if (f.project_id.trim()) form.append("project_id", f.project_id.trim());
    if (f.android_app_id.trim()) form.append("android_app_id", f.android_app_id.trim());
    if (f.ios_app_id.trim()) form.append("ios_app_id", f.ios_app_id.trim());
    if (f.android_package_name.trim()) form.append("android_package_name", f.android_package_name.trim());
    if (f.ios_bundle_id.trim()) form.append("ios_bundle_id", f.ios_bundle_id.trim());
    if (f.service_account_json.trim()) {
      try { JSON.parse(f.service_account_json.trim()); }
      catch { toast.error("Invalid service-account JSON"); return; }
      form.append("service_account_json", f.service_account_json.trim());
    }
    if ([...form.keys()].length === 0) {
      toast.error("Nothing to save — fill at least one field");
      return;
    }
    const exec = () =>
      run("firebase-manual-save", async () => {
        try {
          const { data } = await api.post(`/superadmin/tenants/${tenantId}/firebase/manual`, form);
          const gen = data.generated || {};
          toast.success(
            gen.android || gen.ios
              ? "Manual config saved — real google-services.json / plist fetched from Firebase"
              : "Manual config updated"
          );
          setManualForm((prev) => ({ ...prev, service_account_json: "", web_api_key: "" }));
          await refreshDetail();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Save failed");
        }
      });

    // Warn when overwriting an EXISTING manual Firebase project — changing
    // project_id / App IDs after the tenant already has installed apps
    // silently breaks push delivery until users update from Play/App Store.
    const existing = firebaseStatus?.manual || {};
    const hasExisting = !!existing.project_id;
    const changedProject = f.project_id.trim() && existing.project_id &&
                           f.project_id.trim() !== existing.project_id;
    const changedAndroidApp = f.android_app_id.trim() && existing.android_app_id &&
                              f.android_app_id.trim() !== existing.android_app_id;
    const changedIosApp = f.ios_app_id.trim() && existing.ios_app_id &&
                          f.ios_app_id.trim() !== existing.ios_app_id;
    if (!hasExisting || (!changedProject && !changedAndroidApp && !changedIosApp && !f.service_account_json.trim())) {
      // First save OR purely a fill-in update with no project/app-id change — no warning needed.
      exec();
      return;
    }
    const changes = [];
    if (changedProject) changes.push(`Project ID: ${existing.project_id} → ${f.project_id.trim()}`);
    if (changedAndroidApp) changes.push(`Android App ID: ${existing.android_app_id} → ${f.android_app_id.trim()}`);
    if (changedIosApp) changes.push(`iOS App ID: ${existing.ios_app_id} → ${f.ios_app_id.trim()}`);
    if (f.service_account_json.trim()) changes.push("Service account JSON: replacing");
    confirmFirebaseChange({
      title: "Overwrite existing Firebase configuration?",
      summary: `This tenant already ships with a manual Firebase project. Changing it now affects every installed device.`,
      bullets: [
        ...changes,
        "Installed apps keep their current FCM token but will stop receiving pushes from the new project until users update from Play/App Store.",
        "Re-run `python scripts/build_app.py --tenant <slug>` so the freshly fetched google-services.json / plist is baked into the next native build.",
      ],
      confirmLabel: "Yes, overwrite",
      onConfirm: exec,
    });
  };

  const onManualSaFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setManualForm((prev) => ({ ...prev, service_account_json: text }));
    try {
      const j = JSON.parse(text);
      if (j.project_id) {
        setManualForm((prev) => ({
          ...prev,
          project_id: prev.project_id || j.project_id,
        }));
      }
    } catch { /* ignore */ }
  };

  const revertToAutoMode = () =>
    run("firebase-manual-revert", async () => {
      const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
      if (!tenantId) return;
      const hasExisting = !!firebaseStatus?.manual?.project_id;
      const msg = hasExisting
        ? "Switch this tenant back to AUTO mode? The manual project ID, App IDs and service-account will be removed. Existing google-services.json / GoogleService-Info.plist files will be preserved."
        : "Switch to AUTO mode?";
      if (!window.confirm(msg)) return;
      try {
        await api.delete(`/superadmin/tenants/${tenantId}/firebase/manual`);
        toast.success("Reverted to Auto mode");
        await refreshDetail();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Revert failed");
      }
    });


  const [mobileAppName, setMobileAppName] = useState("");
  // Keep the local input in sync when the panel switches tenants.
  useEffect(() => {
    setMobileAppName(detail?.tenant_record?.mobile_app_name || "");
  }, [detail?.tenant_record?.id, detail?.tenant_record?.mobile_app_name]);

  const saveMobileAppName = () =>
    run("mobile-app-name", async () => {
      const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
      if (!tenantId) { toast.error("Tenant id missing"); return; }
      const next = (mobileAppName || "").trim();
      try {
        await api.put(`/superadmin/tenants/${tenantId}/mobile-app-name`, {
          mobile_app_name: next,
        });
        toast.success(next
          ? `App name set to "${next}". Re-run scripts/build_app.py to bake into the next build.`
          : "Cleared — will fall back to the tenant's primary name.");
        await refreshDetail();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Save failed");
      }
    });

  const uploadTenantLogo = async (file) => {
    const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
    if (!tenantId || !file) return;
    // Pre-flight dimension check on the client so the user gets immediate feedback
    const dims = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.width, h: img.height });
      img.onerror = () => resolve(null);
      img.src = URL.createObjectURL(file);
    });
    if (!dims) { toast.error("Could not read image"); return; }
    if (dims.w !== 1024 || dims.h !== 1024) {
      toast.error(`Logo must be exactly 1024×1024 — got ${dims.w}×${dims.h}`);
      return;
    }
    setBusy("logo");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        `/superadmin/tenants/${tenantId}/logo`, form
      );
      toast.success(`Uploaded to S3 (${data.bucket}) — ${(data.size / 1024).toFixed(1)} KB`);
      await refreshDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "S3 upload failed");
    } finally { setBusy(null); }
  };

  const clearTenantLogo = async () => {
    const tenantId = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
    if (!tenantId) return;
    if (!window.confirm("Remove this tenant's logo? You'll need to upload a new one before the next build.")) return;
    setBusy("logo-clear");
    try {
      await api.delete(`/superadmin/tenants/${tenantId}/logo`);
      toast.success("Logo removed");
      await refreshDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    } finally { setBusy(null); }
  };

  const grantFeature = () =>
    run("grant", async () => {
      if (!grantKey) { toast.error("Pick a feature"); return; }
      await api.post(`/superadmin/tenants/${selectedId}/features/${grantKey}`, {
        billing_cycle: grantCycle,
        quantity: Number(grantQty) || 1,
        price_override: grantPrice === "" ? null : Number(grantPrice),
        note: grantNote || null,
      });
      toast.success("Feature granted");
      setGrantKey(""); setGrantPrice(""); setGrantNote(""); setGrantQty(1);
      await refreshDetail();
    });

  const updateAddon = (feature_key, patch) =>
    run(`update-${feature_key}`, async () => {
      await api.patch(`/superadmin/tenants/${selectedId}/features/${feature_key}`, patch);
      toast.success("Add-on updated");
      await refreshDetail();
    });

  const cancelAddon = (feature_key) =>
    run(`cancel-${feature_key}`, async () => {
      await api.delete(`/superadmin/tenants/${selectedId}/features/${feature_key}`);
      toast.success("Add-on cancelled");
      await refreshDetail();
    });

  const generateInvoice = () =>
    run("invoice", async () => {
      await api.post(`/superadmin/tenants/${selectedId}/invoices/generate`);
      toast.success("Invoice generated");
      await refreshDetail();
    });

  const markPaid = (invoiceId) =>
    run(`paid-${invoiceId}`, async () => {
      await api.post(`/superadmin/tenants/${selectedId}/invoices/${invoiceId}/mark-paid`);
      toast.success("Marked paid");
      await refreshDetail();
    });

  const deleteInvoice = (invoiceId) =>
    run(`del-${invoiceId}`, async () => {
      if (!window.confirm("Delete this invoice permanently? This can't be undone.")) return;
      await api.delete(`/superadmin/tenants/${selectedId}/invoices/${invoiceId}`);
      toast.success("Invoice deleted");
      await refreshDetail();
    });

  const regenerateInvoice = () =>
    run("regen", async () => {
      const markPaidNow = window.confirm(
        "Regenerate the current bill into a fresh invoice?\n\n" +
        "OK = create as PENDING\nCancel = create as PAID immediately",
      );
      // If user clicks OK we create pending; Cancel means they confirmed
      // mark-paid (a slightly unusual UX, but functional). Most use cases
      // create pending — keep that as the default.
      const { data } = await api.post(
        `/superadmin/tenants/${selectedId}/invoices/regenerate`,
        null,
        { params: { mark_paid: !markPaidNow } },
      );
      toast.success(`Regenerated invoice ₹${data.total_amount?.toLocaleString()}`);
      await refreshDetail();
    });

  const grantableFeatures = useMemo(() => {
    const subscribed = new Set((detail?.addons || []).map((a) => a.feature_key));
    return (features || [])
      .filter((f) => f.is_active && !f.is_core_feature && !subscribed.has(f.feature_key));
  }, [features, detail]);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const onPickLogo = async (file) => {
    if (!file) { setLogoFile(null); setLogoPreview(null); return; }
    const dims = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.width, h: img.height });
      img.onerror = () => resolve(null);
      img.src = URL.createObjectURL(file);
    });
    if (!dims || dims.w !== 1024 || dims.h !== 1024) {
      toast.error(`Logo must be exactly 1024×1024 — got ${dims?.w || "?"}×${dims?.h || "?"}`);
      setLogoFile(null); setLogoPreview(null);
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const createTenant = async () => {
    if (!createForm.slug || !createForm.name || !createForm.owner_phone) {
      toast.error("Slug, name and owner phone are required");
      return;
    }
    if (!logoFile) {
      toast.error("Tenant logo is required (1024×1024 PNG/JPG)");
      return;
    }
    setBusy("create");
    try {
      const payload = { ...createForm };
      if (!payload.custom_domain) delete payload.custom_domain;
      if (!payload.owner_name) delete payload.owner_name;
      // Only send `clone` block if at least one category is checked
      if (Object.values(cloneOpts).some(Boolean)) {
        payload.clone = cloneOpts;
      }
      const { data } = await api.post("/superadmin/tenants/create", payload);
      const tenantId = data.tenant.id;

      // Upload logo to S3 (mandatory)
      let logoResult = null;
      try {
        const form = new FormData();
        form.append("file", logoFile);
        const { data: logoData } = await api.post(
          `/superadmin/tenants/${tenantId}/logo`, form
        );
        logoResult = logoData;
        toast.success(`Logo uploaded to AWS S3 (${(logoData.size / 1024).toFixed(1)} KB)`);
      } catch (e) {
        toast.error(`Tenant created but logo upload failed: ${e?.response?.data?.detail || e.message}`);
      }

      let provisioned = null;
      if (autoProvisionFirebase) {
        try {
          const { data: prov } = await api.post(
            `/superadmin/tenants/${tenantId}/firebase/provision`
          );
          provisioned = prov;
          toast.success(`Firebase provisioned`);
        } catch (e) {
          toast.error(`Firebase provision failed: ${e?.response?.data?.detail || e.message}`);
        }
      }
      setCreateResult({ ...data, provisioned, logo: logoResult });
      setLogoFile(null);
      setLogoPreview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create tenant");
    } finally { setBusy(null); }
  };

  const approvePending = async (tid) =>
    run(`approve-${tid}`, async () => {
      await api.post(`/superadmin/tenants/${tid}/approve`);
      toast.success("Tenant approved");
      await Promise.all([loadPending(), load()]);
    });

  const rejectPending = async (tid) =>
    run(`reject-${tid}`, async () => {
      await api.post(`/superadmin/tenants/${tid}/reject`);
      toast.success("Tenant rejected");
      await Promise.all([loadPending(), load()]);
    });

  return (
    <div className="space-y-6" data-testid="super-tenants-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl">Tenants</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
            Create new tenants, suspend / activate, grant features, override pricing and bill any tenant.
          </p>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={() => setShowClone(true)}
            className="kb-btn-outline text-sm inline-flex items-center gap-1.5"
            data-testid="clone-tenant-btn"
            title="Pull a tenant from another deployment into this one">
            <Copy size={14} /> Clone tenant
          </button>
          <button
            onClick={() => { setShowCreate(true); setCreateResult(null); }}
            className="kb-btn-primary text-sm inline-flex items-center gap-1.5"
            data-testid="create-tenant-btn">
            <Plus size={14} /> Create tenant
          </button>
        </div>
      </div>

      <TenantStatusBoard />

      {pending.length > 0 && (
        <section className="bg-amber-50 border-2 border-amber-300 p-5" data-testid="pending-tenants-section">
          <h2 className="font-display text-xl mb-3 flex items-center gap-2 text-amber-900">
            <AlertCircle size={18} /> {pending.length} pending tenant{pending.length > 1 ? "s" : ""} awaiting your approval
          </h2>
          <div className="space-y-2">
            {pending.map((t) => (
              <div key={t.id} className="bg-white border border-amber-200 p-4 flex flex-wrap items-center justify-between gap-3"
                   data-testid={`pending-tenant-${t.slug}`}>
                <div>
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">
                    /t/{t.slug}/ · phone {t.owner_phone} · plan {t.requested_plan_slug || "starter"}
                    {t.business_email ? ` · ${t.business_email}` : ""}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approvePending(t.id)} disabled={busy === `approve-${t.id}`}
                          className="kb-btn-primary text-sm inline-flex items-center gap-1"
                          data-testid={`approve-${t.slug}`}>
                    {busy === `approve-${t.id}` ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve
                  </button>
                  <button onClick={() => rejectPending(t.id)} disabled={busy === `reject-${t.id}`}
                          className="kb-btn-outline text-sm inline-flex items-center gap-1"
                          data-testid={`reject-${t.slug}`}>
                    {busy === `reject-${t.id}` ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-32 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((t) => (
            <button key={t.id} onClick={() => open(t.id)}
              className="bg-white border border-[hsl(var(--kb-border))] p-5 text-left hover:border-[hsl(var(--kb-primary))]/40 transition"
              data-testid={`tenant-card-${t.id}`}>
              <div className="flex items-center gap-2 text-xs text-[hsl(var(--kb-muted))] justify-between">
                <span className="flex items-center gap-1"><Users size={14} /> Tenant</span>
                <StatusPill active={!t.is_suspended} />
              </div>
              <div className="font-display text-xl mt-1">{t.name || "Unnamed"}</div>
              <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">{t.phone}</div>
              {t.tenant_slug && (
                <div className="text-xs text-[hsl(var(--kb-muted))] mt-1 flex items-center gap-1.5"
                     data-testid={`tenant-slug-${t.tenant_slug}`}>
                  <span className="uppercase tracking-wider text-[10px]">Slug:</span>
                  <span className="font-mono">{t.tenant_slug}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(t.tenant_slug);
                      toast.success("Slug copied");
                    }}
                    className="text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))] p-0.5"
                    title="Copy slug"
                    data-testid={`copy-slug-${t.tenant_slug}`}
                  >
                    <Copy size={10} />
                  </button>
                </div>
              )}
              {t.storefront_url && (
                <div className="text-xs text-[hsl(var(--kb-primary))] mt-1.5 flex items-center gap-1.5 flex-wrap"
                     data-testid={`tenant-storefront-url-${t.tenant_slug || t.id}`}>
                  <LinkIcon size={10} className="shrink-0" />
                  <a
                    href={t.storefront_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="underline hover:text-[hsl(var(--kb-primary-hover))] inline-flex items-center gap-1 truncate max-w-[200px]"
                    title={t.storefront_url}
                    data-testid={`visit-storefront-${t.tenant_slug || t.id}`}
                  >
                    {t.storefront_url.replace(/^https?:\/\//, "")}
                    <ExternalLink size={9} className="shrink-0" />
                  </a>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(t.storefront_url);
                      toast.success("URL copied");
                    }}
                    className="text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))] p-0.5"
                    title="Copy URL"
                    data-testid={`copy-storefront-${t.tenant_slug || t.id}`}
                  >
                    <Copy size={10} />
                  </button>
                </div>
              )}
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex items-center gap-1"><CheckCircle2 size={12} className="text-green-600" /> Plan: <b>{t.plan_slug || "—"}</b> ({t.billing_cycle || "—"})</div>
                <div>Active features: {t.active_feature_count}</div>
                <div className="flex items-center gap-1" data-testid={`tenant-card-reels-${t.id}`}>
                  <Clapperboard size={12} className={t.reels_enabled === false ? "text-[hsl(var(--kb-muted))]" : "text-green-600"} />
                  Reels: <b>{t.reels_enabled === false ? "OFF" : "ON"}</b>
                </div>
                {t.admin_usage && (
                  <div className="flex items-center gap-1" data-testid={`tenant-card-admins-${t.id}`}>
                    <Users size={12} className={t.admin_usage.over_limit ? "text-red-600" : ""} />
                    Admins: <b>{t.admin_usage.active} / {t.admin_usage.limit}</b>
                  </div>
                )}
                <div className="flex items-center gap-1 flex-wrap" data-testid={`tenant-card-notif-${t.id}`}>
                  <Bell size={12} className={t.bulk_notifications_enabled || t.scheduled_notifications_enabled ? "text-green-600" : "text-[hsl(var(--kb-muted))]"} />
                  {NOTIFICATION_FEATURES.map((f, i) => (
                    <span key={f.key}>
                      {i > 0 && <span className="text-[hsl(var(--kb-muted))]"> · </span>}
                      {f.short}: <b data-testid={`tenant-card-notif-${f.testid}-${t.id}`}>{t[f.key] ? "ON" : "OFF"}</b>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-1"><TrendingUp size={12} /> Lifetime: ₹{t.lifetime_revenue.toLocaleString()}</div>
                {(t.discount_percent > 0 || t.discount_amount > 0) ? (
                  <div className="flex items-center gap-1 text-blue-700"><Percent size={12} /> Discount applied</div>
                ) : null}
                {t.outstanding_amount > 0 && (
                  <div className="flex items-center gap-1 text-amber-700"><AlertCircle size={12} /> Outstanding: ₹{t.outstanding_amount.toLocaleString()}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={close}>
          <div className="bg-white max-w-5xl w-full max-h-[92vh] overflow-y-auto p-6 space-y-6"
               onClick={(e) => e.stopPropagation()} data-testid="tenant-detail-dialog">
            {!detail ? (
              <div className="h-48 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
            ) : (
              <>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-display text-2xl">{detail.tenant.name}</h2>
                      <StatusPill active={!detail.is_suspended} />
                      <button
                        type="button"
                        onClick={impersonateTenant}
                        disabled={busy === "impersonate"}
                        className="kb-btn-outline !py-1 !px-3 !text-[11px] inline-flex items-center gap-1.5 ml-auto"
                        data-testid="tenant-impersonate-btn"
                        title="Log in as this tenant's admin"
                      >
                        {busy === "impersonate" ? "Logging in…" : "Log in as admin"}
                      </button>
                    </div>
                    <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">{detail.tenant.phone} · {detail.tenant.id}</div>
                    {detail.tenant_record?.slug && (
                      <div className="text-xs text-[hsl(var(--kb-muted))] mt-1 flex items-center gap-1.5"
                           data-testid="tenant-detail-slug">
                        <span className="uppercase tracking-wider text-[10px]">Slug:</span>
                        <span className="font-mono">{detail.tenant_record.slug}</span>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(detail.tenant_record.slug);
                            toast.success("Slug copied");
                          }}
                          className="text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]"
                          title="Copy slug"
                          data-testid="tenant-detail-copy-slug">
                          <Copy size={11} />
                        </button>
                      </div>
                    )}
                    {detail.storefront_url && (
                      <div className="text-xs text-[hsl(var(--kb-primary))] mt-1 flex items-center gap-2 flex-wrap"
                           data-testid="tenant-detail-storefront-url">
                        <LinkIcon size={11} />
                        <a href={detail.storefront_url} target="_blank" rel="noreferrer"
                           className="underline hover:text-[hsl(var(--kb-primary-hover))] inline-flex items-center gap-1">
                          {detail.storefront_url} <ExternalLink size={10} />
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(detail.storefront_url);
                            toast.success("URL copied");
                          }}
                          className="text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]"
                          title="Copy URL"
                          data-testid="tenant-detail-copy-url">
                          <Copy size={11} />
                        </button>
                      </div>
                    )}
                    {detail.tenant.tenant_suspended_reason && (
                      <div className="text-xs text-red-700 mt-1">Reason: {detail.tenant.tenant_suspended_reason}</div>
                    )}
                    {detail.legal_urls && (detail.legal_urls.privacy || detail.legal_urls.terms) && (
                      <div
                        className="text-xs text-[hsl(var(--kb-muted))] mt-3 border-t border-[hsl(var(--kb-border))] pt-3 space-y-1"
                        data-testid="tenant-detail-legal-urls"
                      >
                        <div className="overline mb-1">Legal page URLs</div>
                        {[
                          ["Privacy Policy", detail.legal_urls.privacy, "privacy"],
                          ["Terms & Conditions", detail.legal_urls.terms, "terms"],
                          ["Shipping Policy", detail.legal_urls.shipping, "shipping"],
                          ["Refund Policy", detail.legal_urls.refund, "refund"],
                        ].map(([label, href, kind]) =>
                          href ? (
                            <div key={kind} className="flex items-center gap-2 flex-wrap">
                              <span className="w-36 inline-block">{label}:</span>
                              <a
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[hsl(var(--kb-primary))] underline inline-flex items-center gap-1 font-mono"
                                data-testid={`tenant-legal-${kind}-link`}
                              >
                                {href} <ExternalLink size={10} />
                              </a>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(href);
                                  toast.success(`${label} URL copied`);
                                }}
                                className="text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]"
                                title="Copy URL"
                                data-testid={`tenant-legal-${kind}-copy`}
                              >
                                <Copy size={11} />
                              </button>
                            </div>
                          ) : null
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={toggleSuspend}
                      disabled={busy === "suspend"}
                      className={`text-sm px-3 py-1.5 inline-flex items-center gap-1.5 ${detail.is_suspended ? "kb-btn-primary" : "kb-btn-outline"}`}
                      data-testid="tenant-suspend-toggle">
                      {busy === "suspend" ? <Loader2 size={12} className="animate-spin" /> : (detail.is_suspended ? <Play size={12} /> : <Pause size={12} />)}
                      {detail.is_suspended ? "Activate" : "Disable"}
                    </button>
                    {detail.tenant_record?.slug && detail.tenant_record.slug !== "default" && (
                      <button
                        onClick={deleteTenant}
                        disabled={busy === "delete"}
                        className="text-sm px-3 py-1.5 inline-flex items-center gap-1.5 border border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                        data-testid="tenant-delete-btn">
                        {busy === "delete" ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                        Delete
                      </button>
                    )}
                    <button onClick={close} className="kb-btn-outline text-sm" data-testid="tenant-dialog-close"><X size={14} /></button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="border border-[hsl(var(--kb-border))] p-3"><div className="text-xs text-[hsl(var(--kb-muted))]">Plan</div><div className="font-medium">{detail.subscription?.plan_slug || "—"}</div></div>
                  <div className="border border-[hsl(var(--kb-border))] p-3"><div className="text-xs text-[hsl(var(--kb-muted))]">Cycle</div><div className="font-medium">{detail.subscription?.billing_cycle || "—"}</div></div>
                  <div className="border border-[hsl(var(--kb-border))] p-3"><div className="text-xs text-[hsl(var(--kb-muted))]">Add-ons</div><div className="font-medium">{detail.addons?.length || 0}</div></div>
                  <div className="border border-[hsl(var(--kb-border))] p-3"><div className="text-xs text-[hsl(var(--kb-muted))]">Active features</div><div className="font-medium">{detail.active_features?.length || 0}</div></div>
                </div>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="plan-override-section">
                  <h3 className="font-display text-lg flex items-center gap-2"><Edit3 size={14} /> Plan & price</h3>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">Switch this tenant to a different plan, or override their price / cycle in place.</p>
                  <div className="mt-3 flex flex-wrap items-end gap-3">
                    <label className="text-xs flex flex-col">
                      <span>Plan</span>
                      <select value={planSlugEdit} onChange={(e) => setPlanSlugEdit(e.target.value)} className="kb-input w-44" data-testid="plan-slug-select">
                        <option value="">— keep current —</option>
                        {plans.map((pl) => (
                          <option key={pl.slug} value={pl.slug}>
                            {pl.plan_name} ({pl.slug})
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs flex flex-col">
                      <span>Price (₹)</span>
                      <input type="number" value={planPrice} onChange={(e) => setPlanPrice(e.target.value)} className="kb-input w-32" data-testid="plan-price-input" />
                    </label>
                    <label className="text-xs flex flex-col">
                      <span>Cycle</span>
                      <select value={planCycle} onChange={(e) => setPlanCycle(e.target.value)} className="kb-input w-32" data-testid="plan-cycle-input">
                        {CYCLES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </label>
                    <button className="kb-btn-primary text-sm" onClick={savePlan} disabled={busy === "plan"} data-testid="plan-save-btn">
                      {busy === "plan" ? <Loader2 size={12} className="animate-spin" /> : null} Save
                    </button>
                  </div>
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="discount-section">
                  <h3 className="font-display text-lg flex items-center gap-2"><Percent size={14} /> Discount</h3>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">Applied to every invoice as a negative line item.</p>
                  <div className="mt-3 flex flex-wrap items-end gap-3">
                    <label className="text-xs flex flex-col">
                      <span>Percent (%)</span>
                      <input type="number" min="0" max="100" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} className="kb-input w-28" data-testid="discount-pct-input" />
                    </label>
                    <label className="text-xs flex flex-col">
                      <span>Flat (₹)</span>
                      <input type="number" min="0" value={discountAmt} onChange={(e) => setDiscountAmt(e.target.value)} className="kb-input w-28" data-testid="discount-amt-input" />
                    </label>
                    <label className="text-xs flex flex-col flex-1 min-w-[180px]">
                      <span>Note</span>
                      <input value={discountNote} onChange={(e) => setDiscountNote(e.target.value)} className="kb-input" placeholder="e.g. Loyalty bonus" data-testid="discount-note-input" />
                    </label>
                    <button className="kb-btn-primary text-sm" onClick={saveDiscount} disabled={busy === "discount"} data-testid="discount-save-btn">
                      {busy === "discount" ? <Loader2 size={12} className="animate-spin" /> : null} Save
                    </button>
                    <button className="kb-btn-outline text-sm" onClick={clearDiscount} disabled={busy === "clearDiscount"} data-testid="discount-clear-btn">Clear</button>
                  </div>
                  {detail.estimate?.discount_amount > 0 && (
                    <div className="mt-2 text-xs text-blue-700">Current cycle savings: ₹{detail.estimate.discount_amount.toLocaleString()}</div>
                  )}
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="business-mode-section">
                  <h3 className="font-display text-lg flex items-center gap-2">
                    <Store size={14} /> Storefront mode
                  </h3>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">
                    <b>Multi vendor</b> — sellers self-register and create
                    products; the tenant admin moderates.
                    <br />
                    <b>Single vendor</b> — the tenant admin is the sole seller.
                    Sellers section of the admin UI is hidden.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {["multi_vendor", "single_vendor"].map((m) => {
                      const current = detail.tenant_record?.business_mode || "multi_vendor";
                      const active = current === m;
                      return (
                        <button
                          key={m}
                          onClick={() => !active && saveBusinessMode(m)}
                          disabled={busy === "businessMode"}
                          className={`text-sm px-3 py-1.5 ${active ? "kb-btn-primary" : "kb-btn-outline"}`}
                          data-testid={`business-mode-${m.replace("_", "-")}`}
                        >
                          {busy === "businessMode" && !active ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
                          {m === "single_vendor" ? "Single vendor" : "Multi vendor"}
                        </button>
                      );
                    })}
                    <span className="text-xs text-[hsl(var(--kb-muted))] ml-2">
                      Active: <b>{(detail.tenant_record?.business_mode || "multi_vendor").replace("_", " ")}</b>
                    </span>
                  </div>
                </section>

                <AdminLimitEditor tenantRecord={detail.tenant_record} usage={detail.admin_usage}
                  busy={busy === "adminLimit"} onSave={saveAdminLimit} />

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="notifications-section">
                  <h3 className="font-display text-lg flex items-center gap-2">
                    <Bell size={14} /> Notifications
                  </h3>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">
                    Applies to <b>this tenant only</b>. The two switches are independent.
                    Turning one off hides it from the tenant admin and blocks its API; nothing is
                    deleted, and pending schedules that fall due while off are skipped, not sent late.
                  </p>
                  <div className="mt-3 divide-y divide-[hsl(var(--kb-border))]">
                    {NOTIFICATION_FEATURES.map((f) => {
                      const current = detail.tenant_record?.[f.key] === true;
                      const busyKey = `notif:${f.key}`;
                      return (
                        <div key={f.key} className="py-3 first:pt-0 last:pb-0" data-testid={`tenant-notif-${f.testid}`}>
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0 flex-1 basis-56">
                              <div className="text-sm font-medium">{f.label}</div>
                              <div className="text-xs text-[hsl(var(--kb-muted))]">{f.help}</div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {[true, false].map((v) => {
                                const active = current === v;
                                return (
                                  <button
                                    key={String(v)}
                                    type="button"
                                    onClick={() => !active && saveNotificationFeature(f.key, v)}
                                    disabled={busy === busyKey}
                                    aria-pressed={active}
                                    className={`text-sm px-3 py-1.5 min-h-[40px] ${active ? "kb-btn-primary" : "kb-btn-outline"}`}
                                    data-testid={`tenant-notif-${f.testid}-${v ? "on" : "off"}`}
                                  >
                                    {busy === busyKey && !active ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
                                    {v ? "ON" : "OFF"}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div className="text-xs text-[hsl(var(--kb-muted))] mt-1" data-testid={`tenant-notif-${f.testid}-current`}>
                            Active: <b>{current ? "ON" : "OFF"}</b>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="reels-section">
                  <h3 className="font-display text-lg flex items-center gap-2">
                    <Clapperboard size={14} /> Reels / short-video content
                  </h3>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">
                    Applies to <b>this tenant only</b>. Turn OFF before submitting
                    this tenant&apos;s app to Apple / Google if its Reels library
                    contains third-party audio, video or product catalogs they
                    don&apos;t have written rights to (App Store Guideline 5.2.3).
                    <br />
                    Hides the home rail, the PDP &quot;Seen in Reels&quot; rail, the
                    top-nav Reels link, the <code className="font-mono">/reels</code>{" "}
                    route and the mobile menu entry, and empties the public Reels
                    API — for this tenant. <b>Nothing is deleted:</b> the tenant&apos;s
                    Reels library and admin CMS stay intact, so turning it back ON
                    restores everything.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {[true, false].map((v) => {
                      const current = detail.tenant_record?.reels_enabled !== false;
                      const active = current === v;
                      return (
                        <button
                          key={String(v)}
                          onClick={() => !active && saveReelsEnabled(v)}
                          disabled={busy === "reels"}
                          className={`text-sm px-3 py-1.5 ${active ? "kb-btn-primary" : "kb-btn-outline"}`}
                          data-testid={`tenant-reels-${v ? "on" : "off"}`}
                        >
                          {busy === "reels" && !active ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
                          {v ? "Reels ON" : "Reels OFF"}
                        </button>
                      );
                    })}
                    <span className="text-xs text-[hsl(var(--kb-muted))] ml-2"
                          data-testid="tenant-reels-current">
                      Active: <b>{detail.tenant_record?.reels_enabled !== false ? "ON" : "OFF"}</b>
                    </span>
                  </div>
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="clone-from-default-section">
                  <h3 className="font-display text-lg flex items-center gap-2">
                    <Copy size={14} className="text-blue-600" /> Sync catalog from default tenant
                  </h3>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">
                    Pulls the latest items from the <b>default</b> tenant into this
                    storefront. Existing items on this tenant are kept untouched —
                    only missing ones (matched by slug) are added.
                  </p>
                  {detail.tenant_record?.slug === "default" ? (
                    <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">
                      This <b>is</b> the default tenant — cloning onto itself is disabled.
                    </p>
                  ) : (
                    <>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        {[
                          { key: "categories", label: "Categories (full tree)" },
                          { key: "attributes", label: "Attributes & values" },
                          { key: "homepage_sections", label: "Homepage sections" },
                          { key: "brands", label: "Brands" },
                        ].map((opt) => (
                          <label key={opt.key} className="flex items-center gap-2 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!detailCloneOpts[opt.key]}
                              onChange={(e) => setDetailCloneOpts({
                                ...detailCloneOpts, [opt.key]: e.target.checked,
                              })}
                              data-testid={`detail-clone-${opt.key}`}
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                      <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <button
                          onClick={runDetailClone}
                          disabled={busy === "clone" ||
                                    !Object.values(detailCloneOpts).some(Boolean)}
                          className="kb-btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5"
                          data-testid="detail-clone-run"
                        >
                          {busy === "clone"
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Copy size={12} />}
                          Sync from default
                        </button>
                        {detailCloneResult && !detailCloneResult.error && (
                          <span className="text-xs text-green-700">
                            Added: {Object.entries(detailCloneResult)
                              .filter(([, v]) => typeof v === "number" && v > 0)
                              .map(([k, v]) => `${v} ${k.replace("_", " ")}`)
                              .join(", ") || "nothing new"}
                          </span>
                        )}
                        {detailCloneResult?.warning && (
                          <span className="text-xs text-amber-700">
                            ⚠ {detailCloneResult.warning}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="push-section">
                  <h3 className="font-display text-lg flex items-center gap-2">
                    <Bell size={14} className="text-purple-600" /> Push notifications
                  </h3>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">
                    Send a notification to <b>this tenant&apos;s</b> mobile + web devices only.
                    Tokens are strictly scoped to (tenant, device) — Tenant A&apos;s broadcasts can
                    never reach Tenant B&apos;s installs.
                  </p>

                  {pushSummary && !pushSummary.error && (
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="push-summary">
                      <div className="bg-[hsl(var(--kb-surface-alt))] p-2">
                        <div className="overline">Total</div>
                        <div className="text-lg font-display" data-testid="push-summary-total">{pushSummary.total}</div>
                      </div>
                      <div className="bg-[hsl(var(--kb-surface-alt))] p-2">
                        <div className="overline">Android</div>
                        <div className="text-lg font-display">{pushSummary.by_platform?.android || 0}</div>
                      </div>
                      <div className="bg-[hsl(var(--kb-surface-alt))] p-2">
                        <div className="overline">iOS</div>
                        <div className="text-lg font-display">{pushSummary.by_platform?.ios || 0}</div>
                      </div>
                      <div className="bg-[hsl(var(--kb-surface-alt))] p-2">
                        <div className="overline">Web</div>
                        <div className="text-lg font-display">{pushSummary.by_platform?.web || 0}</div>
                      </div>
                    </div>
                  )}

                  {/* iOS-specific banner — fires when no iOS device has ever
                       registered. Almost always means the IPA was signed
                       WITHOUT the `aps-environment` entitlement. */}
                  {pushSummary && pushSummary.total > 0 &&
                   (pushSummary.by_platform?.ios || 0) === 0 && (
                    <div
                      className="mt-3 border border-amber-300 bg-amber-50 p-3 text-xs"
                      data-testid="push-ios-zero-warning"
                    >
                      <div className="font-medium text-amber-900 mb-1">
                        iOS shows 0 devices — but Android works
                      </div>
                      <div className="text-amber-900">
                        No iOS device has ever called <code className="font-mono">/api/devices/register</code>.
                        The Xcode console for a logged-in iOS user should print{" "}
                        <code className="font-mono">[mobilePush] registration ← token …XXX</code>.
                        If it stops at <code className="font-mono">Push.register() → asking APNs/FCM for a token</code>{" "}
                        with no follow-up, the signed IPA is missing the{" "}
                        <code className="font-mono">aps-environment</code> entitlement.
                      </div>
                      <div className="mt-2 text-amber-900">
                        On your Mac, after archiving but BEFORE uploading to
                        TestFlight, run:
                        <pre className="mt-1 bg-amber-100 p-2 overflow-x-auto whitespace-pre">scripts/verify_ios_entitlements.sh path/to/App.ipa</pre>
                        It returns PASS only when the signed binary contains
                        the entitlement. If it fails, follow the printed
                        Xcode + Apple Developer Portal steps.
                      </div>
                    </div>
                  )}

                  <div className="mt-4 space-y-3">
                    <input
                      className="kb-input w-full"
                      placeholder="Notification title (max 120 chars)"
                      maxLength={120}
                      value={pushForm.title}
                      onChange={(e) => setPushForm({ ...pushForm, title: e.target.value })}
                      data-testid="push-input-title"
                    />
                    <textarea
                      rows={2}
                      className="kb-input w-full"
                      placeholder="Notification body (max 400 chars)"
                      maxLength={400}
                      value={pushForm.body}
                      onChange={(e) => setPushForm({ ...pushForm, body: e.target.value })}
                      data-testid="push-input-body"
                    />
                    <div className="flex items-center gap-4 text-xs">
                      <span className="overline">Send to:</span>
                      {["android", "ios", "web"].map((p) => (
                        <label key={p} className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!pushForm.platforms[p]}
                            onChange={(e) => setPushForm({
                              ...pushForm,
                              platforms: { ...pushForm.platforms, [p]: e.target.checked },
                            })}
                            data-testid={`push-platform-${p}`}
                          />
                          <span className="capitalize">{p}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => sendPush(true)}
                        disabled={busy === "pushPreview" || busy === "pushSend"}
                        className="kb-btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5"
                        data-testid="push-preview-btn"
                      >
                        {busy === "pushPreview"
                          ? <Loader2 size={12} className="animate-spin" />
                          : <Bell size={12} />}
                        Preview reach
                      </button>
                      <button
                        onClick={() => sendPush(false)}
                        disabled={busy === "pushPreview" || busy === "pushSend"
                                  || !pushForm.title.trim() || !pushForm.body.trim()}
                        className="kb-btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5"
                        data-testid="push-send-btn"
                      >
                        {busy === "pushSend"
                          ? <Loader2 size={12} className="animate-spin" />
                          : <Bell size={12} />}
                        Send broadcast
                      </button>
                      {pushResult && !pushResult.dry_run && (
                        <span className="text-xs text-green-700" data-testid="push-result">
                          ✓ Sent {pushResult.sent}/{pushResult.matched}
                          {pushResult.failed > 0 && ` · ${pushResult.failed} failed`}
                          {pushResult.stale_cleaned > 0 && ` · ${pushResult.stale_cleaned} stale removed`}
                        </span>
                      )}
                      {pushResult?.dry_run && (
                        <span className="text-xs text-blue-700" data-testid="push-preview-result">
                          🔍 Will reach {pushResult.matched} device(s)
                          {pushResult.by_platform_matched && (
                            <> ({Object.entries(pushResult.by_platform_matched)
                              .map(([k, v]) => `${v} ${k}`).join(", ")})</>
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  {pushHistory.length > 0 && (
                    <div className="mt-4 border-t border-[hsl(var(--kb-border))] pt-3">
                      <div className="overline mb-2">Recent broadcasts — click a row for raw FCM message_ids</div>
                      <div className="space-y-1 max-h-48 overflow-y-auto text-xs" data-testid="push-history">
                        {pushHistory.map((h) => (
                          <div key={h.id}>
                            <button
                              type="button"
                              className="w-full flex items-baseline gap-3 py-1 border-b border-[hsl(var(--kb-border))] last:border-0 text-left hover:bg-[hsl(var(--kb-surface-alt))] px-1"
                              onClick={() => {
                                const tid = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
                                if (tid) loadBroadcastDetail(tid, h.id);
                              }}
                              data-testid={`push-history-row-${h.id}`}
                            >
                              <span className="text-[hsl(var(--kb-muted))] tabular-nums text-[10px]">
                                {(h.created_at || "").slice(0, 16).replace("T", " ")}
                              </span>
                              <span className="flex-1 truncate font-medium">{h.title}</span>
                              <span className="text-green-700">{h.sent}</span>
                              <span className="text-[hsl(var(--kb-muted))]">/{h.matched}</span>
                              {h.failed > 0 && <span className="text-red-700">·{h.failed}f</span>}
                              {h.dry_run && <span className="text-blue-700 text-[10px]">preview</span>}
                            </button>
                            {pushBroadcastDetail?.id === h.id && (
                              <div className="ml-3 my-2 border-l-2 border-[hsl(var(--kb-primary))] pl-3 text-[10px] font-mono space-y-1" data-testid={`push-history-detail-${h.id}`}>
                                {pushBroadcastDetail.loading && <div>loading…</div>}
                                {pushBroadcastDetail.error && (
                                  <div className="text-red-700">{pushBroadcastDetail.error}</div>
                                )}
                                {pushBroadcastDetail.data && (
                                  <>
                                    <div className="text-[hsl(var(--kb-muted))]">
                                      FCM-accepted: {pushBroadcastDetail.data.sent || 0}/{pushBroadcastDetail.data.matched || 0}
                                      {pushBroadcastDetail.data.stale_cleaned > 0 &&
                                        ` · ${pushBroadcastDetail.data.stale_cleaned} stale tokens cleaned`}
                                    </div>
                                    {(pushBroadcastDetail.data.successes || []).map((s, i) => (
                                      <div key={i} className="text-green-800">
                                        <span className="bg-green-100 px-1">{s.platform}</span>{" "}
                                        {s.token_suffix} → {s.message_id || "(no message_id)"}
                                      </div>
                                    ))}
                                    {(pushBroadcastDetail.data.failures || []).map((f, i) => (
                                      <div key={i} className="text-red-700">
                                        <span className="bg-red-100 px-1">{f.platform}</span>{" "}
                                        {f.token_suffix || f.device_id} ✗ {f.error}
                                        {f.stale && " [stale, cleaned]"}
                                      </div>
                                    ))}
                                    {!pushBroadcastDetail.data.successes?.length &&
                                     !pushBroadcastDetail.data.failures?.length && (
                                      <div className="text-[hsl(var(--kb-muted))]">No per-device records (older broadcast or dry-run).</div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ----- Diagnostics: device sample + single-token test send ----- */}
                  <div className="mt-4 border-t border-[hsl(var(--kb-border))] pt-3" data-testid="push-diag">
                    <div className="overline mb-2 flex items-center justify-between">
                      <span>Diagnostics — &ldquo;Sent X/X but nothing arrives&rdquo;</span>
                      <button
                        type="button"
                        onClick={() => {
                          const tid = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
                          if (tid) loadPushDeviceSample(tid);
                        }}
                        className="text-[hsl(var(--kb-primary))] text-[10px] hover:underline"
                        data-testid="push-diag-load-sample"
                      >
                        Load token sample
                      </button>
                    </div>
                    {pushDiag?.sample?.length > 0 && (
                      <div className="text-[10px] font-mono space-y-1 mb-3 max-h-40 overflow-y-auto" data-testid="push-diag-sample">
                        {pushDiag.sample.map((d) => (
                          <div key={d.device_id} className="flex items-center gap-2 py-0.5 border-b border-[hsl(var(--kb-border))] last:border-0">
                            <span className="bg-[hsl(var(--kb-surface-alt))] px-1.5 py-0.5 uppercase">{d.platform}</span>
                            <span className="text-[hsl(var(--kb-muted))]">{d.token_suffix}</span>
                            <span className="text-[hsl(var(--kb-muted))]">{d.token_length}ch</span>
                            <span className="flex-1 truncate text-[hsl(var(--kb-muted))]">{d.app_id || "—"}</span>
                            <span className="text-[hsl(var(--kb-muted))]">v{d.app_version || "?"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="space-y-2">
                      <textarea
                        rows={2}
                        className="kb-input w-full text-[10px] font-mono"
                        placeholder="Paste a full FCM/APNs token from a device log (mobilePush.js logs the last 12 chars; tap the device to find the full token via adb logcat / Xcode console)"
                        value={pushTestToken}
                        onChange={(e) => setPushTestToken(e.target.value)}
                        data-testid="push-diag-token-input"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const tid = detail?.tenant_record?.id || detail?.tenant?.tenant_id;
                          if (tid) sendPushTest(tid);
                        }}
                        disabled={pushDiag?.test_busy || !pushTestToken.trim()}
                        className="kb-btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5"
                        data-testid="push-diag-send-test"
                      >
                        {pushDiag?.test_busy
                          ? <Loader2 size={12} className="animate-spin" />
                          : <Bell size={12} />}
                        Send single test (returns raw FCM response)
                      </button>
                      {pushDiag?.test_result && (
                        <div
                          className={`text-[10px] font-mono p-2 border ${
                            pushDiag.test_result.ok
                              ? "border-green-300 bg-green-50 text-green-900"
                              : "border-red-300 bg-red-50 text-red-900"
                          }`}
                          data-testid="push-diag-test-result"
                        >
                          <div className="font-bold mb-1">
                            {pushDiag.test_result.ok ? "✓ FCM accepted" : "✗ FCM rejected"}
                            {typeof pushDiag.test_result.fcm_status === "number" &&
                              ` (HTTP ${pushDiag.test_result.fcm_status})`}
                          </div>
                          {pushDiag.test_result.message_id && (
                            <div className="break-all">message_id: {pushDiag.test_result.message_id}</div>
                          )}
                          {pushDiag.test_result.error && (
                            <div className="break-all">error: {pushDiag.test_result.error}</div>
                          )}
                          {pushDiag.test_result.note && (
                            <div className="mt-1 text-[hsl(var(--kb-muted))] whitespace-normal">{pushDiag.test_result.note}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="logo-section">
                  <h3 className="font-display text-lg flex items-center gap-2">
                    <ImageIcon size={14} className="text-blue-600" /> Tenant logo
                    <span className="text-xs text-red-600">*</span>
                  </h3>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">
                    Required <b>1024×1024</b> PNG/JPG, stored on AWS S3 (<code>{detail.tenant_record?.logo_storage_key ? "follomedia/" : ""}</code>).
                    Used to generate the App icon (512×512) and Play Store feature graphic (1024×500).
                  </p>
                  {detail.tenant_record?.logo_url ? (
                    <div className="mt-3 flex items-start gap-4 flex-wrap">
                      <a href={detail.tenant_record.logo_url} target="_blank" rel="noreferrer"
                         className="block border border-[hsl(var(--kb-border))] hover:opacity-90"
                         data-testid="tenant-logo-preview">
                        <img src={detail.tenant_record.logo_url}
                             alt="Tenant logo"
                             className="w-24 h-24 object-cover bg-[hsl(var(--kb-surface-alt))]" />
                      </a>
                      <div className="flex-1 min-w-[260px] text-xs space-y-1">
                        <div className="inline-flex items-center gap-1 text-green-700 font-medium">
                          <CheckCircle2 size={12} /> Uploaded to AWS S3
                        </div>
                        <div className="font-mono text-[10px] text-[hsl(var(--kb-muted))] break-all">
                          {detail.tenant_record.logo_url}
                          <button
                            type="button"
                            onClick={() => { navigator.clipboard.writeText(detail.tenant_record.logo_url); toast.success("URL copied"); }}
                            className="ml-1 text-[hsl(var(--kb-primary))] hover:underline"
                            data-testid="tenant-logo-copy-url">
                            <Copy size={9} className="inline" /> copy
                          </button>
                        </div>
                        {detail.tenant_record.logo_uploaded_at && (
                          <div className="text-[10px] text-[hsl(var(--kb-muted))]">
                            uploaded {new Date(detail.tenant_record.logo_uploaded_at).toLocaleString()}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <input id="tenant-logo-input" type="file" accept="image/png,image/jpeg"
                                 className="hidden"
                                 onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadTenantLogo(f); e.target.value = ""; }}
                                 data-testid="tenant-logo-file-input" />
                          <label htmlFor="tenant-logo-input"
                                 className={`text-xs inline-flex items-center gap-1 px-2 py-1 cursor-pointer border border-[hsl(var(--kb-border))] bg-white hover:bg-[hsl(var(--kb-surface))] ${busy === "logo" ? "opacity-50 pointer-events-none" : ""}`}
                                 data-testid="tenant-logo-replace-btn">
                            {busy === "logo" ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
                            Replace
                          </label>
                          <button onClick={clearTenantLogo} disabled={busy === "logo-clear"}
                                  className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                                  data-testid="tenant-logo-clear-btn">
                            {busy === "logo-clear" ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 border border-dashed border-red-300 bg-red-50 p-4 text-center">
                      <AlertCircle size={20} className="mx-auto text-red-500 mb-2" />
                      <div className="text-sm text-red-800 font-medium">No logo uploaded</div>
                      <div className="text-xs text-red-700 mb-3">
                        Upload a 1024×1024 logo before running the build script.
                      </div>
                      <input id="tenant-logo-input-empty" type="file" accept="image/png,image/jpeg"
                             className="hidden"
                             onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadTenantLogo(f); e.target.value = ""; }}
                             data-testid="tenant-logo-file-input-empty" />
                      <label htmlFor="tenant-logo-input-empty"
                             className={`kb-btn-primary text-sm inline-flex items-center gap-1.5 cursor-pointer ${busy === "logo" ? "opacity-50 pointer-events-none" : ""}`}
                             data-testid="tenant-logo-upload-btn">
                        {busy === "logo" ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                        Upload logo
                      </label>
                    </div>
                  )}
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="firebase-section">
                  <h3 className="font-display text-lg flex items-center gap-2">
                    <Flame size={14} className="text-orange-600" /> Mobile app / Firebase
                  </h3>
                  <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">
                    Auto-provision creates apps in the assigned Firebase shard. You can also
                    upload <code>google-services.json</code> and <code>GoogleService-Info.plist</code> manually
                    — the build script picks up whichever pair is present.
                  </p>

                  {/* ---- Mobile App display name ------------------------------------------ */}
                  <div
                    className="mt-4 border border-[hsl(var(--kb-border))] p-3 bg-[hsl(var(--kb-surface-alt))]"
                    data-testid="mobile-app-name-card"
                  >
                    <div className="flex items-center gap-2">
                      <Smartphone size={13} className="text-[hsl(var(--kb-muted))]" />
                      <span className="text-sm font-medium">App display name</span>
                    </div>
                    <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-1">
                      Used as the <strong>home-screen icon label</strong> on both Android &amp; iOS,
                      and inside the app (header, splash screen, profile / About page). One value
                      per tenant — leave empty to fall back to the tenant&apos;s name
                      (<code className="font-mono">{detail.tenant_record?.name}</code>).
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="text"
                        value={mobileAppName}
                        onChange={(e) => setMobileAppName(e.target.value)}
                        placeholder={detail.tenant_record?.name || "e.g. Acme Store"}
                        maxLength={30}
                        className="flex-1 border border-[hsl(var(--kb-border))] p-1.5 text-sm font-mono"
                        data-testid="mobile-app-name-input"
                      />
                      <button
                        onClick={saveMobileAppName}
                        disabled={busy === "mobile-app-name"
                                  || mobileAppName === (detail.tenant_record?.mobile_app_name || "")}
                        className="kb-btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid="mobile-app-name-save"
                      >
                        {busy === "mobile-app-name"
                          ? <Loader2 size={11} className="animate-spin" />
                          : <BadgeCheck size={11} />}
                        Save
                      </button>
                    </div>
                    <div className="mt-1.5 text-[10px] text-[hsl(var(--kb-muted))]">
                      Max 30 characters. iOS HIG recommends ≤ 12 chars to avoid icon-label
                      truncation. Effective name in the next build:{" "}
                      <strong className="font-mono">
                        {(mobileAppName || "").trim() || detail.tenant_record?.name || "GetOwnApp Commerce"}
                      </strong>
                    </div>
                    {detail.tenant_record?.mobile_app_name && (
                      <div className="mt-1 text-[10px] text-amber-700">
                        ⚠ Re-run <code className="font-mono">python scripts/build_app.py --tenant {detail.tenant_record?.slug}</code>{" "}
                        to apply this name to the next Android/iOS build. Existing installed
                        apps keep their old name until users update.
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    {/* Mode badge */}
                    <span
                      className={`text-[10px] px-2 py-1 inline-flex items-center gap-1 font-medium ${
                        firebaseStatus?.mode === "manual"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-emerald-100 text-emerald-800"
                      }`}
                      data-testid="firebase-mode-badge"
                    >
                      {firebaseStatus?.mode === "manual"
                        ? "MANUAL — existing Firebase project"
                        : "AUTO — shard-provisioned"}
                    </span>
                    {firebaseStatus?.mode === "manual" ? (
                      <button
                        type="button"
                        onClick={revertToAutoMode}
                        disabled={busy === "firebase-manual-revert"}
                        className="kb-btn-outline text-xs inline-flex items-center gap-1"
                        data-testid="firebase-revert-auto-btn"
                      >
                        {busy === "firebase-manual-revert"
                          ? <Loader2 size={11} className="animate-spin" />
                          : <Flame size={11} />}
                        Switch to Auto
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          if (detail?.tenant_record?.firebase_provisioned_at) {
                            setShowManualWarning(true);
                          } else {
                            setPendingManualSwitch(true);
                          }
                        }}
                        className="kb-btn-outline text-xs inline-flex items-center gap-1"
                        data-testid="firebase-switch-manual-btn"
                      >
                        Use existing Firebase project
                      </button>
                    )}
                  </div>

                  {(firebaseStatus?.mode === "manual" || pendingManualSwitch) && (
                    <div
                      className="mt-4 border border-blue-300 bg-blue-50/40 p-4 space-y-3"
                      data-testid="firebase-manual-panel"
                    >
                      <div className="flex items-start gap-2">
                        <Flame size={14} className="text-blue-700 mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-sm">Manual Firebase configuration</div>
                          <div className="text-[11px] text-[hsl(var(--kb-muted))] mt-0.5">
                            Bring your own Firebase project. Enter the Android &amp; iOS App IDs from
                            your Firebase console and upload the service-account JSON — we&apos;ll
                            fetch the canonical <code>google-services.json</code> /
                            <code>GoogleService-Info.plist</code> directly from Firebase (with the
                            real API key, OAuth clients and analytics IDs baked in) and stash them
                            for the build script. Partial updates preserve existing values.
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <label className="flex flex-col gap-1 text-xs">
                          <span>Project ID *</span>
                          <input
                            className="kb-input"
                            placeholder="my-firebase-project"
                            value={manualForm.project_id}
                            onChange={(e) => setManualForm({ ...manualForm, project_id: e.target.value })}
                            data-testid="manual-project-id-input"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-xs">
                          <span>Messaging Sender ID (auto-detected)</span>
                          <input
                            className="kb-input font-mono bg-[hsl(var(--kb-surface-alt))]"
                            placeholder="(derived from App IDs)"
                            value={firebaseStatus?.manual?.messaging_sender_id || ""}
                            disabled
                            data-testid="manual-sender-id-display"
                          />
                        </label>

                        <label className="flex flex-col gap-1 text-xs">
                          <span className="flex items-center gap-1"><Smartphone size={10} /> Android App ID</span>
                          <input
                            className="kb-input font-mono"
                            placeholder="1:1234567890:android:abcdef0123"
                            value={manualForm.android_app_id}
                            onChange={(e) => setManualForm({ ...manualForm, android_app_id: e.target.value })}
                            data-testid="manual-android-app-id-input"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-xs">
                          <span className="flex items-center gap-1"><Apple size={10} /> iOS App ID</span>
                          <input
                            className="kb-input font-mono"
                            placeholder="1:1234567890:ios:fedcba9876"
                            value={manualForm.ios_app_id}
                            onChange={(e) => setManualForm({ ...manualForm, ios_app_id: e.target.value })}
                            data-testid="manual-ios-app-id-input"
                          />
                        </label>

                        <label className="flex flex-col gap-1 text-xs">
                          <span>Android Package Name</span>
                          <input
                            className="kb-input font-mono"
                            placeholder="com.example.store"
                            value={manualForm.android_package_name}
                            onChange={(e) => setManualForm({ ...manualForm, android_package_name: e.target.value })}
                            data-testid="manual-android-package-input"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-xs">
                          <span>iOS Bundle ID</span>
                          <input
                            className="kb-input font-mono"
                            placeholder="com.example.store"
                            value={manualForm.ios_bundle_id}
                            onChange={(e) => setManualForm({ ...manualForm, ios_bundle_id: e.target.value })}
                            data-testid="manual-ios-bundle-input"
                          />
                        </label>

                        <label className="flex flex-col gap-1 text-xs md:col-span-2">
                          <span>Service Account requirements</span>
                          <div className="text-[11px] text-[hsl(var(--kb-muted))] p-2 bg-amber-50 border border-amber-200">
                            The service account needs the <b>Firebase Admin</b> role in Google Cloud IAM
                            so we can read the project&apos;s real <code>google-services.json</code> and
                            <code>GoogleService-Info.plist</code> directly from the Firebase Management API.
                            We fetch the canonical files — no placeholders — so the iOS Firebase SDK
                            won&apos;t reject the API key.
                          </div>
                        </label>
                      </div>

                      <div className="border-t border-blue-200 pt-3 space-y-2">
                        <div className="text-xs font-medium flex items-center gap-2">
                          Service Account JSON
                          {firebaseStatus?.manual?.sa_present && (
                            <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5">
                              ON FILE ({firebaseStatus.manual.sa_email})
                            </span>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="application/json,.json"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) onManualSaFile(f); e.target.value = ""; }}
                          className="kb-input text-xs"
                          data-testid="manual-sa-file-input"
                        />
                        <div className="text-[10px] text-[hsl(var(--kb-muted))] text-center">— or paste JSON —</div>
                        <textarea
                          rows={4}
                          className="kb-input font-mono text-[11px] w-full"
                          placeholder='{ "type": "service_account", "project_id": "...", ... }'
                          value={manualForm.service_account_json}
                          onChange={(e) => setManualForm({ ...manualForm, service_account_json: e.target.value })}
                          data-testid="manual-sa-textarea"
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={saveManualFirebase}
                          disabled={busy === "firebase-manual-save"}
                          className="kb-btn-primary text-xs inline-flex items-center gap-1.5"
                          data-testid="manual-save-btn"
                        >
                          {busy === "firebase-manual-save"
                            ? <Loader2 size={11} className="animate-spin" />
                            : <BadgeCheck size={11} />}
                          Save manual config
                        </button>
                        {pendingManualSwitch && firebaseStatus?.mode !== "manual" && (
                          <button
                            type="button"
                            onClick={() => setPendingManualSwitch(false)}
                            className="kb-btn-outline text-xs"
                            data-testid="manual-cancel-btn"
                          >Cancel</button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Warning before mutating an already-live Firebase config:
                       re-provision, replace google-services.json/plist, or
                       overwrite manual project_id. */}
                  {fbWarn && (
                    <div
                      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                      onClick={() => setFbWarn(null)}
                      data-testid="firebase-change-warning-modal"
                    >
                      <div className="bg-white max-w-lg w-full p-5 space-y-3"
                           onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <AlertCircle size={18} className="text-amber-600" />
                          <h3 className="font-display text-lg" data-testid="firebase-warn-title">
                            {fbWarn.title}
                          </h3>
                        </div>
                        <p className="text-sm text-[hsl(var(--kb-muted))]">
                          {fbWarn.summary}
                        </p>
                        {fbWarn.bullets?.length > 0 && (
                          <ul className="text-xs text-[hsl(var(--kb-muted))] list-disc pl-5 space-y-1 bg-amber-50 border border-amber-200 p-3">
                            {fbWarn.bullets.map((b, i) => (
                              <li key={i}>{b}</li>
                            ))}
                          </ul>
                        )}
                        <div className="flex items-center justify-end gap-2 pt-2">
                          <button
                            onClick={() => setFbWarn(null)}
                            className="kb-btn-outline text-sm"
                            data-testid="firebase-warn-cancel">
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              const fn = fbWarn.onConfirm;
                              setFbWarn(null);
                              fn?.();
                            }}
                            className="kb-btn-primary text-sm"
                            data-testid="firebase-warn-confirm">
                            {fbWarn.confirmLabel || "Continue"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Warning before switching modes on already-provisioned tenant */}
                  {showManualWarning && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                         onClick={() => setShowManualWarning(false)}
                         data-testid="manual-warning-modal">
                      <div className="bg-white max-w-md w-full p-5 space-y-3"
                           onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <AlertCircle size={18} className="text-amber-600" />
                          <h3 className="font-display text-lg">Switch Firebase mode?</h3>
                        </div>
                        <p className="text-sm text-[hsl(var(--kb-muted))]">
                          This tenant is currently <b>auto-provisioned</b> in our Firebase shard.
                          Switching to manual mode will route push notifications through your
                          own Firebase project instead. Existing installed apps with the auto
                          push tokens may need to refresh their FCM token after the next build.
                        </p>
                        <div className="flex items-center justify-end gap-2 pt-2">
                          <button
                            onClick={() => setShowManualWarning(false)}
                            className="kb-btn-outline text-sm"
                            data-testid="manual-warning-cancel">
                            Cancel
                          </button>
                          <button
                            onClick={() => { setShowManualWarning(false); setPendingManualSwitch(true); }}
                            className="kb-btn-primary text-sm"
                            data-testid="manual-warning-continue">
                            Continue to manual setup
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <button
                      onClick={provisionFirebase}
                      disabled={busy === "firebase" || firebaseStatus?.mode === "manual"}
                      className="kb-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="firebase-provision-btn"
                      title={firebaseStatus?.mode === "manual" ? "Tenant is in manual mode — switch to Auto first" : ""}
                    >
                      {busy === "firebase" ? <Loader2 size={12} className="animate-spin" /> : <Flame size={12} />}
                      {detail.tenant_record?.firebase_provisioned_at ? "Re-provision (auto)" : "Auto-provision in Firebase"}
                    </button>
                    {detail.tenant_record?.firebase_provisioned_at && (
                      <span className="text-xs text-green-700 inline-flex items-center gap-1">
                        <CheckCircle2 size={11} /> last provisioned {new Date(detail.tenant_record.firebase_provisioned_at).toLocaleString()}
                      </span>
                    )}
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <FirebaseUploadTile
                      platform="android"
                      label="google-services.json"
                      icon={Smartphone}
                      bundleId={firebaseStatus?.android?.package || detail.tenant_record?.android_package_name}
                      status={firebaseStatus?.android}
                      busy={busy}
                      onUpload={(f) => uploadFirebaseConfig("android", f)}
                      onClear={() => clearFirebaseConfig("android")}
                    />
                    <FirebaseUploadTile
                      platform="ios"
                      label="GoogleService-Info.plist"
                      icon={Apple}
                      bundleId={firebaseStatus?.ios?.bundle_id || detail.tenant_record?.ios_bundle_id}
                      status={firebaseStatus?.ios}
                      busy={busy}
                      onUpload={(f) => uploadFirebaseConfig("ios", f)}
                      onClear={() => clearFirebaseConfig("ios")}
                    />
                  </div>
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="addons-section">
                  <h3 className="font-display text-lg flex items-center gap-2"><BadgeCheck size={14} /> Active add-ons</h3>
                  {detail.addons?.length ? (
                    <div className="mt-3 space-y-2">
                      {detail.addons.map((a) => (
                        <AddonRow key={a.id} addon={a}
                                  onSave={(patch) => updateAddon(a.feature_key, patch)}
                                  onCancel={() => cancelAddon(a.feature_key)}
                                  busy={busy} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-[hsl(var(--kb-muted))] mt-2">No add-ons.</div>
                  )}
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="grant-section">
                  <h3 className="font-display text-lg flex items-center gap-2"><Plus size={14} /> Grant a feature manually</h3>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-5 gap-3">
                    <select value={grantKey} onChange={(e) => setGrantKey(e.target.value)} className="kb-input md:col-span-2" data-testid="grant-feature-select">
                      <option value="">— pick a feature —</option>
                      {grantableFeatures.map((f) => <option key={f.feature_key} value={f.feature_key}>{f.feature_name}</option>)}
                    </select>
                    <select value={grantCycle} onChange={(e) => setGrantCycle(e.target.value)} className="kb-input" data-testid="grant-cycle-select">
                      {CYCLES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input type="number" min="1" value={grantQty} onChange={(e) => setGrantQty(e.target.value)} className="kb-input" placeholder="Qty" data-testid="grant-qty-input" />
                    <input type="number" min="0" value={grantPrice} onChange={(e) => setGrantPrice(e.target.value)} className="kb-input" placeholder="Price (₹) — blank = catalog" data-testid="grant-price-input" />
                  </div>
                  <input value={grantNote} onChange={(e) => setGrantNote(e.target.value)} className="kb-input mt-3 w-full" placeholder="Internal note (optional)" data-testid="grant-note-input" />
                  <button className="kb-btn-primary text-sm mt-3" onClick={grantFeature} disabled={busy === "grant" || !grantKey} data-testid="grant-submit-btn">
                    {busy === "grant" ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Grant
                  </button>
                </section>

                <section className="border border-[hsl(var(--kb-border))] p-4" data-testid="invoices-section">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-display text-lg flex items-center gap-2"><FileText size={14} /> Invoices</h3>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs text-[hsl(var(--kb-muted))]">Next bill estimate: <b>₹{detail.estimate?.total_amount?.toLocaleString() || 0}</b></span>
                      <button className="kb-btn-outline text-sm" onClick={generateInvoice} disabled={busy === "invoice"} data-testid="generate-invoice-btn">
                        {busy === "invoice" ? <Loader2 size={12} className="animate-spin" /> : null}
                        Generate invoice now
                      </button>
                      <button
                        className="kb-btn-outline text-sm"
                        onClick={regenerateInvoice}
                        disabled={busy === "regen"}
                        data-testid="regenerate-invoice-btn"
                        title="Materialise the current bill preview into a fresh invoice"
                      >
                        {busy === "regen" ? <Loader2 size={12} className="animate-spin" /> : null}
                        Regenerate
                      </button>
                    </div>
                  </div>
                  {detail.invoices?.length === 0 ? (
                    <div className="text-sm text-[hsl(var(--kb-muted))] mt-2">No invoices yet.</div>
                  ) : (
                    <div className="mt-3 space-y-2">
                      {detail.invoices.map((inv) => (
                        <div key={inv.id} className="border border-[hsl(var(--kb-border))] p-3 flex items-center justify-between text-sm flex-wrap gap-2" data-testid={`invoice-${inv.id}`}>
                          <div>
                            <div className="font-medium">₹{inv.total_amount.toLocaleString()} · {inv.billing_cycle}</div>
                            <div className="text-xs text-[hsl(var(--kb-muted))]">
                              {new Date(inv.billing_start_date).toLocaleDateString()} – {new Date(inv.billing_end_date).toLocaleDateString()} · {inv.line_items?.length || 0} line items
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs px-2 py-0.5 ${inv.status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{inv.status}</span>
                            {inv.status !== "paid" && (
                              <button className="kb-btn-outline text-xs" onClick={() => markPaid(inv.id)} disabled={busy === `paid-${inv.id}`} data-testid={`invoice-mark-paid-${inv.id}`}>
                                {busy === `paid-${inv.id}` ? <Loader2 size={10} className="animate-spin" /> : null} Mark paid
                              </button>
                            )}
                            <button
                              className="text-xs text-red-700 hover:underline"
                              onClick={() => deleteInvoice(inv.id)}
                              disabled={busy === `del-${inv.id}`}
                              data-testid={`invoice-delete-${inv.id}`}
                              title="Delete this invoice"
                            >
                              {busy === `del-${inv.id}` ? <Loader2 size={10} className="animate-spin inline" /> : "Delete"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        </div>
      )}
      {showClone && (
        <CloneTenantModal
          onClose={() => setShowClone(false)}
          onSuccess={() => load()}
        />
      )}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
             onClick={() => setShowCreate(false)}>
          <div className="bg-white max-w-lg w-full p-6 space-y-4"
               onClick={(e) => e.stopPropagation()} data-testid="create-tenant-dialog">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl">Create a tenant</h2>
              <button onClick={() => setShowCreate(false)} className="kb-btn-outline text-sm" data-testid="create-tenant-close"><X size={14} /></button>
            </div>
            {createResult ? (
              <div className="space-y-3" data-testid="create-tenant-result">
                <div className="bg-green-50 border border-green-200 p-4 text-sm">
                  <div className="font-semibold text-green-800">✓ {createResult.tenant.name} is ready.</div>
                  <div className="mt-3 space-y-1 text-green-900 font-mono text-xs">
                    <div>Slug: <b>{createResult.tenant.slug}</b></div>
                    <div className="flex items-center gap-2 flex-wrap">
                      Storefront URL:
                      <a className="underline break-all" href={createResult.storefront_url || `/t/${createResult.tenant.slug}/`} target="_blank" rel="noreferrer" data-testid="create-tenant-result-url">
                        {createResult.storefront_url || `/t/${createResult.tenant.slug}/`} <ExternalLink size={10} className="inline" />
                      </a>
                      {createResult.storefront_url && (
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(createResult.storefront_url);
                            toast.success("URL copied");
                          }}
                          className="text-green-700 hover:text-green-900"
                          title="Copy URL"
                          data-testid="create-tenant-copy-url">
                          <Copy size={11} />
                        </button>
                      )}
                    </div>
                    <div>Admin phone: <b>{createResult.owner.phone}</b></div>
                  </div>
                  <p className="mt-3 text-xs text-green-700">Share the storefront URL and admin login with the tenant. They sign in with the phone above and OTP shown in the modal.</p>
                </div>
                {createResult.clone_summary && !createResult.clone_summary.error && (
                  <div className="bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900" data-testid="create-tenant-clone-result">
                    <div className="font-semibold mb-1">Catalog seeded from default tenant</div>
                    <ul className="space-y-0.5">
                      {Object.entries(createResult.clone_summary)
                        .filter(([k, v]) => typeof v === "number")
                        .map(([k, v]) => (
                          <li key={k}>• {v} {k.replace("_", " ")}</li>
                        ))}
                      {createResult.clone_summary.warning && (
                        <li className="text-amber-700">⚠ {createResult.clone_summary.warning}</li>
                      )}
                    </ul>
                  </div>
                )}
                <button onClick={() => setShowCreate(false)} className="kb-btn-primary text-sm w-full" data-testid="create-tenant-done">Done</button>
              </div>
            ) : (
              <>
                <p className="text-sm text-[hsl(var(--kb-muted))]">Create a brand-new white-label tenant with its own catalog, customers, sellers and branding.</p>
                <label className="text-xs flex flex-col">
                  <span>URL slug (lowercase, dashes only)</span>
                  <input className="kb-input" value={createForm.slug} placeholder="e.g. nayaab-couture"
                         onChange={(e) => setCreateForm({ ...createForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                         data-testid="create-tenant-slug" />
                </label>
                <label className="text-xs flex flex-col">
                  <span>Display name</span>
                  <input className="kb-input" value={createForm.name} placeholder="Nayaab Couture House"
                         onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                         data-testid="create-tenant-name" />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs flex flex-col">
                    <span>Owner phone (10 digit)</span>
                    <input className="kb-input" value={createForm.owner_phone} placeholder="9XXXXXXXXX"
                           onChange={(e) => setCreateForm({ ...createForm, owner_phone: e.target.value.replace(/[^0-9]/g, "") })}
                           data-testid="create-tenant-phone" />
                  </label>
                  <label className="text-xs flex flex-col">
                    <span>Owner name (optional)</span>
                    <input className="kb-input" value={createForm.owner_name}
                           onChange={(e) => setCreateForm({ ...createForm, owner_name: e.target.value })}
                           data-testid="create-tenant-owner-name" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs flex flex-col">
                    <span>Plan</span>
                    <select className="kb-input" value={createForm.plan_slug}
                            onChange={(e) => setCreateForm({ ...createForm, plan_slug: e.target.value })}
                            data-testid="create-tenant-plan">
                      {plans.length === 0 ? (
                        <>
                          <option value="starter">Starter</option>
                          <option value="pro">Pro</option>
                          <option value="enterprise">Enterprise</option>
                        </>
                      ) : (
                        plans.map((pl) => (
                          <option key={pl.slug} value={pl.slug}>
                            {pl.plan_name} ({pl.slug})
                          </option>
                        ))
                      )}
                    </select>
                  </label>
                  <label className="text-xs flex flex-col">
                    <span>Custom domain (optional)</span>
                    <input className="kb-input" value={createForm.custom_domain} placeholder="store.brand.com"
                           onChange={(e) => setCreateForm({ ...createForm, custom_domain: e.target.value.toLowerCase() })}
                           data-testid="create-tenant-domain" />
                  </label>
                </div>

                <div className="border border-blue-200 bg-blue-50 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-blue-900">
                    <ImageIcon size={12} /> Tenant logo
                    <span className="text-red-600">*</span>
                    <span className="text-[10px] font-normal text-blue-700 ml-auto">1024×1024 PNG/JPG</span>
                  </div>
                  <p className="text-[10px] text-blue-700 mt-1">
                    Uploaded to AWS S3 right after the tenant is created. Used to auto-generate
                    the app icon (512×512) and Play Store feature graphic (1024×500).
                  </p>
                  <div className="mt-2 flex items-center gap-3">
                    {logoPreview ? (
                      <img src={logoPreview} alt="logo preview"
                           className="w-16 h-16 object-cover border border-blue-300"
                           data-testid="create-tenant-logo-preview" />
                    ) : (
                      <div className="w-16 h-16 border border-dashed border-blue-300 flex items-center justify-center text-blue-300">
                        <ImageIcon size={20} />
                      </div>
                    )}
                    <div className="flex-1">
                      <input id="create-tenant-logo-input" type="file" accept="image/png,image/jpeg"
                             className="hidden"
                             onChange={(e) => onPickLogo(e.target.files?.[0])}
                             data-testid="create-tenant-logo-file" />
                      <label htmlFor="create-tenant-logo-input"
                             className="text-xs inline-flex items-center gap-1 px-2 py-1.5 cursor-pointer border border-blue-300 bg-white hover:bg-blue-100"
                             data-testid="create-tenant-logo-btn">
                        <Upload size={11} /> {logoFile ? "Replace logo" : "Choose logo"}
                      </label>
                      {logoFile && (
                        <div className="text-[10px] text-green-700 mt-1 inline-flex items-center gap-1">
                          <CheckCircle2 size={10} /> {logoFile.name} ({(logoFile.size / 1024).toFixed(1)} KB)
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] p-3" data-testid="clone-options">
                  <div className="overline mb-2 flex items-center gap-1.5">
                    <Copy size={12} /> Seed catalog from default tenant
                  </div>
                  <p className="text-[11px] text-[hsl(var(--kb-muted))] mb-3">
                    Deep-copies the selected items from the <b>default</b> tenant so the new
                    storefront is non-empty on day one. Hierarchies + slugs are preserved;
                    new IDs are minted.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: "categories", label: "Categories (full tree)" },
                      { key: "attributes", label: "Attributes & values" },
                      { key: "homepage_sections", label: "Homepage sections" },
                      { key: "brands", label: "Brands" },
                    ].map((opt) => (
                      <label key={opt.key} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!cloneOpts[opt.key]}
                          onChange={(e) => setCloneOpts({ ...cloneOpts, [opt.key]: e.target.checked })}
                          data-testid={`clone-${opt.key}`}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-2 text-xs cursor-pointer p-3 bg-orange-50 border border-orange-200">
                  <input type="checkbox" checked={autoProvisionFirebase}
                         onChange={(e) => setAutoProvisionFirebase(e.target.checked)}
                         className="accent-orange-600"
                         data-testid="create-tenant-auto-firebase" />
                  <Flame size={12} className="text-orange-600" />
                  <span className="flex-1">
                    Auto-provision Android &amp; iOS in Firebase
                    <span className="block text-[10px] text-[hsl(var(--kb-muted))] mt-0.5">
                      Creates the apps in the assigned shard so the Capacitor build is ready immediately. Skip if no shards are configured yet.
                    </span>
                  </span>
                </label>
                <button onClick={createTenant} disabled={busy === "create"}
                        className="kb-btn-primary w-full text-sm inline-flex items-center justify-center gap-1.5"
                        data-testid="create-tenant-submit">
                  {busy === "create" ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Create tenant
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AddonRow({ addon, onSave, onCancel, busy }) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(addon.price_at_purchase || 0);
  const [qty, setQty] = useState(addon.quantity || 1);
  const isBusyUpdate = busy === `update-${addon.feature_key}`;
  const isBusyCancel = busy === `cancel-${addon.feature_key}`;

  return (
    <div className="border border-[hsl(var(--kb-border))] p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`addon-row-${addon.feature_key}`}>
      <div className="min-w-0">
        <div className="font-medium">{addon.feature_name}</div>
        <div className="text-xs text-[hsl(var(--kb-muted))] font-mono truncate">{addon.feature_key}</div>
        {addon.granted_by_super_admin && (
          <div className="text-xs text-purple-700 mt-1">Granted by super admin{addon.grant_note ? ` — ${addon.grant_note}` : ""}</div>
        )}
      </div>
      {editing ? (
        <div className="flex items-end gap-2">
          <label className="text-xs flex flex-col">Price ₹
            <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="kb-input w-24" data-testid={`addon-price-${addon.feature_key}`} />
          </label>
          <label className="text-xs flex flex-col">Qty
            <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="kb-input w-16" data-testid={`addon-qty-${addon.feature_key}`} />
          </label>
          <button className="kb-btn-primary text-xs" disabled={isBusyUpdate}
                  onClick={async () => {
                    await onSave({ price_at_purchase: Number(price), quantity: Number(qty) || 1 });
                    setEditing(false);
                  }}
                  data-testid={`addon-save-${addon.feature_key}`}>
            {isBusyUpdate ? <Loader2 size={10} className="animate-spin" /> : "Save"}
          </button>
          <button className="kb-btn-outline text-xs" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="text-sm">₹{addon.price_at_purchase} × {addon.quantity || 1}</div>
          <button className="kb-btn-outline text-xs" onClick={() => setEditing(true)} data-testid={`addon-edit-${addon.feature_key}`}>
            <Edit3 size={10} /> Edit
          </button>
          <button className="kb-btn-outline text-xs" disabled={isBusyCancel} onClick={onCancel} data-testid={`addon-cancel-${addon.feature_key}`}>
            {isBusyCancel ? <Loader2 size={10} className="animate-spin" /> : <X size={10} />}
          </button>
        </div>
      )}
    </div>
  );
}
